import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import GlobalRanking, { calcGlobalLevel, DualLevelUpScreen } from "./GlobalRanking";
import AudioEngine from "./audioEngine";
import { TIPS } from "./tips";
import { getPushStatus, enablePush } from "./pushNotifications";
import { SettingsRow, SettingsInfoPage, SettingsUsernamePage, SettingsXPPage, SettingsContactPage, SettingsAudioPage, SettingsNotificationsPage} from "./Settings";
import AdminDashboard from "./AdminDashboard";
import StatsCharts from "./StatsCharts";
import AuthScreen from "./AuthScreen";
import OnboardingFlow from "./OnboardingFlow";
// ─── Supabase ─────────────────────────────────────────────────────────────────
const SUPABASE_URL = "https://trtieaijkrdipiljyzuh.supabase.co";
const SUPABASE_KEY = "sb_publishable_eJKNPdo5XwSCH-X8Ttantw_feBJed2q";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ─── Config ───────────────────────────────────────────────────────────────────
// Username rules: A-Z, 0-9 only, min 3, max 25, stored+displayed uppercase
function sanitizeName(val){ return (val||"").toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,25); }
function isValidName(val){ return /^[A-Z0-9]{3,25}$/.test((val||"").trim()); }

const CAT = {
  work:     { color:"#3b82f6", bg:"rgba(59,130,246,0.10)",  icon:"💼", label:"Work" },
  personal: { color:"#a855f7", bg:"rgba(168,85,247,0.10)", icon:"🌱", label:"Personal" },
  health:   { color:"#22c55e", bg:"rgba(34,197,94,0.10)",  icon:"💪", label:"Health" },
  learning: { color:"#f59e0b", bg:"rgba(245,158,11,0.10)", icon:"📚", label:"Learning" },
  creative: { color:"#ec4899", bg:"rgba(236,72,153,0.10)", icon:"🎨", label:"Creative" },
  other:    { color:"#94a3b8", bg:"rgba(148,163,184,0.10)", icon:"⚡", label:"Other" },
};
const LOG_STATUSES = ["In Progress","Review","Done"];
const SCOL = {"Not Started":"#334","In Progress":"#38bdf8","Review":"#f59e0b","Done":"#4ade80"};
const POSITIVE = ["In Progress","Review","Done"];
const PERIODS = ["daily","weekly","monthly","yearly"];
const PERIOD_LABELS = {daily:"Today",weekly:"This Week",monthly:"This Month",yearly:"This Year"};
const SYS = "#38bdf8"; // system blue
const SYS2 = "#0ea5e9";
const SYS_DIM = "rgba(56,189,248,0.12)";
const SYS_BORDER = "rgba(56,189,248,0.25)";
const WARN = "#ef4444";

const METRICS = {
  time:       {icon:"⏱", label:"Time Spent",      unit:"",      isDecimal:false},
  pages:      {icon:"📄", label:"Pages Read",      unit:"pages", isDecimal:false},
  reps:       {icon:"🔁", label:"Reps / Sets",     unit:"reps",  isDecimal:false},
  km:         {icon:"🏃", label:"Distance (km)",   unit:"km",    isDecimal:true },
  words:      {icon:"✍️", label:"Words Written",   unit:"words", isDecimal:false},
  tasks_done: {icon:"✅", label:"Completions", unit:"done", isDecimal:false},
  custom:     {icon:"🎯", label:"Custom",          unit:"",      isDecimal:true },
};

const SEED = [
  {id:"t1",createdAt:new Date(Date.now()-6*86400000).toISOString(),title:"Gym",     desc:"Track gym sessions.",          cat:"health",  status:"Not Started",progress:0,  logs:[],total:0, metric:"custom",     customUnit:"sessions", goals:{weekly:{amount:"5"}}},
  {id:"t2",createdAt:new Date(Date.now()-6*86400000).toISOString(),title:"Content", desc:"Track posts published.",        cat:"creative",status:"Not Started",progress:0,  logs:[],total:0, metric:"custom",     customUnit:"posts",    goals:{weekly:{amount:"3"}}},
  {id:"t3",createdAt:new Date(Date.now()-6*86400000).toISOString(),title:"Edit",    desc:"Track editing time.",           cat:"work",    status:"Not Started",progress:0,  logs:[],total:0, metric:"time",       customUnit:"",         goals:{daily:{h:"1",m:"0"}}},
  {id:"t4",createdAt:new Date(Date.now()-6*86400000).toISOString(),title:"Read",    desc:"Track pages read.",             cat:"learning",status:"Not Started",progress:0,  logs:[],total:0, metric:"pages",      customUnit:"",         goals:{daily:{amount:"30"}}},
  {id:"t5",createdAt:new Date(Date.now()-6*86400000).toISOString(),title:"Learn",   desc:"Track learning time.",          cat:"learning",status:"Not Started",progress:0,  logs:[],total:0, metric:"time",       customUnit:"",         goals:{weekly:{h:"5",m:"0"}}},
];


async function loadTasks(userId){
  try{
    const {data}=await supabase.from("tasks").select("*").eq("user_id",userId);
    if(!data||data.length===0)return null;
    return data.map(r=>r.data);
  }catch{return null;}
}
async function saveTask(userId,task){
  try{
    await supabase.from("tasks").upsert({id:task.id,user_id:userId,data:task,updated_at:new Date().toISOString()});
  }catch(e){console.error(e);}
}
// eslint-disable-next-line no-unused-vars
async function deleteTaskDB(userId,taskId){
  try{
    await supabase.from("tasks").delete().eq("id",taskId).eq("user_id",userId);
  }catch(e){console.error(e);}
}
async function loadProfile(userId){
  try{
    const {data}=await supabase.from("profiles").select("*").eq("id",userId).single();
    return data;
  }catch{return null;}
}
async function saveProfile(userId,playerName,email){
  try{
    const row={id:userId,player_name:playerName};
    if(email) row.email=email;
    await supabase.from("profiles").upsert(row);
  }catch(e){console.error(e);}
}

// Write computed global XP/level/topCat via secure server-side function
async function saveGlobalStats(userId, tasks){
  try{
    const GLOBAL_CATS=["work","learning","health","creative"];
    const GLOBAL_STANDARD_XP=100;
    function gGoal(task,period){
      const g=task.goals?.[period];if(!g)return 0;
      if(task.metric==="time")return(parseInt(g.h||0)*60)+parseInt(g.m||0);
      return parseFloat(g.amount||0);
    }
    function gXpForLog(task,log){
      const goal=gGoal(task,"weekly")||gGoal(task,"daily")*7;
      if(!goal||goal<=0)return GLOBAL_STANDARD_XP/10;
      const value=task.metric==="time"?(log.value?.totalMins||0):parseFloat(log.value?.amount||0);
      if(value<=0)return 0;
      return(value/goal)*GLOBAL_STANDARD_XP;
    }
    const globalTasks=tasks.filter(t=>GLOBAL_CATS.includes(t.cat));
    const xp=Math.floor(globalTasks.reduce((s,t)=>s+(t.logs||[]).reduce((ss,l)=>ss+gXpForLog(t,l),0),0));
    const base=GLOBAL_CATS.length*GLOBAL_STANDARD_XP;
    const level=Math.floor(xp/base)+1;
    const xpByCat={};
    GLOBAL_CATS.forEach(c=>{xpByCat[c]=globalTasks.filter(t=>t.cat===c).reduce((s,t)=>(t.logs||[]).reduce((ss,l)=>ss+gXpForLog(t,l),s),0);});
    const topCat=Object.entries(xpByCat).sort((a,b)=>b[1]-a[1])[0]?.[0]||null;
    // Use SECURITY DEFINER function — validates ownership + bounds server-side
    await supabase.rpc("update_global_stats",{
      p_user_id:userId,
      p_xp:xp,
      p_level:level,
      p_top_cat:topCat,
    });
  }catch(e){console.error("saveGlobalStats:",e);}
}


// ─── Offline Cache Helpers ────────────────────────────────────────────────────
const CACHE_KEYS = {
  tasks: (uid) => `arise_tasks_${uid}`,
  profile: (uid) => `arise_profile_${uid}`,
  pendingLogs: (uid) => `arise_pending_${uid}`,
};

function cacheTasks(userId, tasks){
  try{ localStorage.setItem(CACHE_KEYS.tasks(userId), JSON.stringify(tasks)); }
  catch(e){ console.warn("Cache write failed:", e); }
}

function getCachedTasks(userId){
  try{
    const raw = localStorage.getItem(CACHE_KEYS.tasks(userId));
    return raw ? JSON.parse(raw) : null;
  }catch{ return null; }
}

function cacheProfile(userId, data){
  try{ localStorage.setItem(CACHE_KEYS.profile(userId), JSON.stringify(data)); }
  catch(e){ console.warn("Cache write failed:", e); }
}

function getCachedProfile(userId){
  try{
    const raw = localStorage.getItem(CACHE_KEYS.profile(userId));
    return raw ? JSON.parse(raw) : null;
  }catch{ return null; }
}

function queueOfflineLog(userId, taskId, entry){
  try{
    const key = CACHE_KEYS.pendingLogs(userId);
    const existing = JSON.parse(localStorage.getItem(key)||"[]");
    existing.push({taskId, entry, queuedAt: Date.now()});
    localStorage.setItem(key, JSON.stringify(existing));
  }catch(e){ console.warn("Queue write failed:", e); }
}

function getPendingLogs(userId){
  try{ return JSON.parse(localStorage.getItem(CACHE_KEYS.pendingLogs(userId))||"[]"); }
  catch{ return []; }
}

function clearPendingLogs(userId){
  try{ localStorage.removeItem(CACHE_KEYS.pendingLogs(userId)); }
  catch{}
}

function isOnline(){ return navigator.onLine; }
const uid=()=>`${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
function fmtMins(m){if(!m)return"0m";const h=Math.floor(m/60),r=m%60;return h?`${h}h${r?" "+r+"m":""}`:r+"m";}
const fmtDate=iso=>new Date(iso).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"});
function fmtAmount(amount,metric,customUnit){
  if(metric==="time")return fmtMins(amount);
  const u=metric==="custom"?(customUnit||""):METRICS[metric]?.unit||"";
  const n=metric==="km"?parseFloat(amount).toFixed(1):Math.round(amount);
  return u?`${n} ${u}`:String(n);
}

// ─── Date / Period Math ───────────────────────────────────────────────────────
function periodStart(period){
  const n=new Date();
  if(period==="daily"){const d=new Date(n);d.setHours(0,0,0,0);return d;}
  if(period==="weekly"){const d=new Date(n);d.setHours(0,0,0,0);d.setDate(d.getDate()-d.getDay());return d;}
  if(period==="monthly")return new Date(n.getFullYear(),n.getMonth(),1);
  if(period==="yearly")return new Date(n.getFullYear(),0,1);
  return new Date(0);
}
function periodEnd(period){
  const n=new Date();
  if(period==="daily"){const d=new Date(n);d.setHours(23,59,59,999);return d;}
  if(period==="weekly"){const d=new Date(n);d.setHours(23,59,59,999);d.setDate(d.getDate()+(6-d.getDay()));return d;}
  if(period==="monthly")return new Date(n.getFullYear(),n.getMonth()+1,0,23,59,59,999);
  if(period==="yearly")return new Date(n.getFullYear(),11,31,23,59,59,999);
  return new Date();
}
function realDaysInPeriod(period){
  const n=new Date();
  if(period==="daily")return 1;
  if(period==="weekly")return 7;
  if(period==="monthly")return new Date(n.getFullYear(),n.getMonth()+1,0).getDate();
  if(period==="yearly"){const y=n.getFullYear();return((y%4===0&&y%100!==0)||(y%400===0))?366:365;}
  return 1;
}
function daysLeftInPeriod(period){
  return Math.max(0,Math.ceil((periodEnd(period).getTime()-Date.now())/86400000));
}
function logsInPeriod(logs,period,metric){
  const s=periodStart(period).getTime();
  return logs.filter(l=>new Date(l.date).getTime()>=s&&(!metric||l.metric===metric));
}
function sumLogs(logs,metric){
  const matched=logs.filter(l=>(!l.metric||l.metric===metric)&&l.status!=="In Progress"&&l.status!=="Not Started");
  if(metric==="time")return matched.reduce((a,l)=>a+(l.value?.totalMins||0),0);
  return matched.reduce((a,l)=>a+parseFloat(l.value?.amount||0),0);
}

// ─── Goal Math ────────────────────────────────────────────────────────────────
function goalToAmount(goal,metric){
  if(!goal)return 0;
  if(metric==="time")return(parseInt(goal.h||0)*60)+parseInt(goal.m||0);
  return parseFloat(goal.amount||0);
}
const PERIOD_ORDER=["daily","weekly","monthly","yearly"];
function getEffectiveGoalAmt(task,period){
  const goals=task.goals||{};
  const metric=task.metric;
  const own=goalToAmount(goals[period],metric);
  if(own>0)return own;
  if(period==="daily")return 0;
  const periodIdx=PERIOD_ORDER.indexOf(period);
  let best=null,bestDiff=Infinity;
  for(const p of PERIOD_ORDER){
    if(p===period||p==="daily")continue;
    const amt=goalToAmount(goals[p],metric);
    if(amt>0){const diff=Math.abs(PERIOD_ORDER.indexOf(p)-periodIdx);if(diff<bestDiff){bestDiff=diff;best=p;}}
  }
  if(best){
    const rate=goalToAmount(goals[best],metric)/realDaysInPeriod(best);
    return Math.round(rate*realDaysInPeriod(period)*100)/100;
  }
  const dailyAmt=goalToAmount(goals["daily"],metric);
  if(dailyAmt>0){const rate=dailyAmt/realDaysInPeriod("daily");return Math.round(rate*realDaysInPeriod(period)*100)/100;}
  return 0;
}
function getGoalSource(task,period){
  const goals=task.goals||{};
  if(goalToAmount(goals[period],task.metric)>0)return null;
  if(period==="daily")return null;
  const periodIdx=PERIOD_ORDER.indexOf(period);
  let best=null,bestDiff=Infinity;
  for(const p of PERIOD_ORDER){
    if(p===period||p==="daily")continue;
    const amt=goalToAmount(goals[p],task.metric);
    if(amt>0){const diff=Math.abs(PERIOD_ORDER.indexOf(p)-periodIdx);if(diff<bestDiff){bestDiff=diff;best=p;}}
  }
  if(best)return best;
  if(goalToAmount(goals["daily"],task.metric)>0)return "daily";
  return null;
}
function getPeriodProgress(task,period){
  const achieved=sumLogs(logsInPeriod(task.logs,period,task.metric),task.metric);
  const goalAmt=getEffectiveGoalAmt(task,period);
  const source=getGoalSource(task,period);
  const rawPct=goalAmt>0?Math.round((achieved/goalAmt)*100):null;
  const pct=rawPct!==null?Math.min(100,rawPct):null;
  const remaining=goalAmt>0?Math.max(0,Math.round((goalAmt-achieved)*100)/100):null;
  const overage=goalAmt>0&&achieved>goalAmt?Math.round((achieved-goalAmt)*100)/100:0;
  const met=goalAmt>0&&achieved>=goalAmt;
  // Behind pace: compare achieved vs expected based on days active in this period
  let behind=false;
  if(!met&&goalAmt>0){
    const pStart=periodStart(period).getTime();
    const created=task.createdAt?new Date(task.createdAt).getTime():pStart;
    const activeFrom=Math.max(pStart,created); // task started mid-period? use creation date
    const totalPeriodMs=periodEnd(period).getTime()-pStart;
    const elapsedMs=Math.max(0,Date.now()-activeFrom);
    const elapsedFraction=Math.min(1,elapsedMs/totalPeriodMs);
    const expectedByNow=goalAmt*elapsedFraction;
    behind=elapsedFraction>0.1&&achieved<expectedByNow*0.7; // only flag if 30%+ behind expected
  }
  return{achieved,goalAmt,pct,rawPct,remaining,overage,hasGoal:goalAmt>0,met,behind,isDerived:!!source,source};
}

// ─── Awakening Screen ─────────────────────────────────────────────────────────
// Computes font size for player name: scales down for long names to fit mobile
function nameSize(name){
  // letterSpacing:8 adds 8px per char on top of char width (~0.6×fontSize)
  // totalWidth = n*(fontSize*0.6 + 8) ≤ 310px → fontSize = (310/n - 8)/0.6
  const n = Math.max(1,(name||"PLAYER").length);
  return Math.max(14, Math.min(42, Math.floor((310/n - 8)/0.6)));
}

// Dynamic font size for home screen name (letterSpacing:1, shares row with Lv.X)
function homeNameSize(name){
  // Available width for name ~160px (accounts for sword, Lv button, padding)
  const n = Math.max(1,(name||"PLAYER").length);
  return Math.max(12, Math.min(26, Math.floor((160/n - 1)/0.6)));
}

function AwakeningScreen({onDone, playerName, fromLogin, isPremium, level=1}){
  const name = (playerName||"PLAYER").toUpperCase();
  const GOLD = "#fbbf24";
  // typedChars[i] = number of chars revealed for line i (only typed lines)
  const [typedChars, setTypedChars] = useState({});
  const [phase,      setPhase]      = useState(0);  // lines faded in (by index+1)
  const [tapReady,   setTapReady]   = useState(false);
  const timersRef = useRef([]);

  const CHAR_MS  = 50;  // ms per character
  const LINE_GAP = 180; // pause between typed lines

  // typed:true → typewriter effect on login; fade otherwise
  const lines = [
    {text:"A.R.I.S.E SYSTEM INITIALIZING", color:SYS,   size:12, spacing:4},
    {text:"WELCOME BACK",                  color:"#fff", size:22, spacing:6, typed:true},
    {text:name,                            color:isPremium?GOLD:SYS, size:nameSize(name), spacing:8, isName:true, isPremiumName:isPremium},
    {text:"[ NEW DAY PROTOCOL ACTIVE ]",   color:SYS,   size:11, spacing:4},
    {text:"YOUR TARGETS AWAIT.",           color:"#aaa", size:14, spacing:3, typed:true},
    {text:"WILL YOU ANSWER THE CALL?",     color:"#aaa", size:13, spacing:3, typed:true},
  ];

  function addTimer(fn, ms){ const id=setTimeout(fn,ms); timersRef.current.push(id); return id; }

  useEffect(()=>{
    AudioEngine.preload();
    timersRef.current=[];

    if(!fromLogin){
      // ── REFRESH: simple staggered fade-in + opening sound ──
      const delays=[0,500,1000,1700,2300,2900];
      lines.forEach((_,i)=> addTimer(()=>setPhase(p=>Math.max(p,i+1)), delays[i]));
      addTimer(()=>AudioEngine.awakening(), 120);
      addTimer(()=>setTapReady(true), 3400);
      return ()=>{ timersRef.current.forEach(clearTimeout); };
    }

    // ── LOGIN: typed lines chain sequentially, fade lines appear instantly ──
    addTimer(()=>AudioEngine.awakening(), 80);

    // Build a timeline: for each line, compute when it starts appearing
    // typed lines: start showing when previous typed line finishes
    // fade lines: appear at a fixed offset after the previous typed line starts
    let typedCursor = 300; // when the first typed line starts (ms)

    // pre-compute start times for all lines
    const startAt = new Array(lines.length).fill(0);
    // fade lines that appear before typing starts
    startAt[0] = 0;   // "A.R.I.S.E..." fades in immediately
    startAt[2] = 0;   // player name fades in immediately
    startAt[3] = 0;   // "[ NEW DAY... ]" fades in immediately

    // typed lines chain: 1 → 4 → 5
    const typedIndices = lines.map((l,i)=>l.typed?i:-1).filter(i=>i>=0); // [1,4,5]
    typedIndices.forEach((lineIdx, order)=>{
      startAt[lineIdx] = typedCursor;
      typedCursor += lines[lineIdx].text.length * CHAR_MS + LINE_GAP;
    });

    // Schedule phase reveals (opacity) and character-by-character typing
    lines.forEach((l,i)=>{
      // Make the container visible
      addTimer(()=>setPhase(p=>Math.max(p,i+1)), startAt[i]);

      if(l.typed){
        // Schedule each character
        l.text.split('').forEach((ch, ci)=>{
          addTimer(()=>{
            setTypedChars(prev=>({...prev,[i]:ci+1}));
            if(ch!==' ') AudioEngine.typeChar();
          }, startAt[i] + (ci+1)*CHAR_MS);
        });
      }
    });

    // TAP TO ENTER after last typed line finishes
    addTimer(()=>setTapReady(true), typedCursor + 200);

    return ()=>{ timersRef.current.forEach(clearTimeout); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  return(
    <div onClick={onDone} style={{position:"fixed",inset:0,background:"#000",zIndex:9999,
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      cursor:"pointer",overflow:"hidden"}}>
      <div style={{position:"absolute",inset:0,
        backgroundImage:"repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.15) 2px,rgba(0,0,0,0.15) 4px)",
        pointerEvents:"none",zIndex:1}}/>
      <div style={{position:"absolute",inset:0,
        background:"radial-gradient(ellipse at center, rgba(56,189,248,0.08) 0%, transparent 70%)",
        pointerEvents:"none",zIndex:1}}/>
      <div style={{position:"relative",zIndex:3,textAlign:"center",
        padding:"0 28px",width:"100%",maxWidth:400,boxSizing:"border-box"}}>
        {lines.map((l,i)=>{
          const visible   = phase > i;
          const chars     = typedChars[i] ?? (l.typed ? 0 : l.text.length);
          const isTyping  = l.typed && fromLogin && chars < l.text.length;
          const displayTxt = (l.typed && fromLogin) ? l.text.slice(0, chars) : l.text;
          return(
            <div key={i} style={{
              fontFamily:"'Share Tech Mono','Courier New',monospace",
              fontSize:l.size,
              letterSpacing:l.spacing,
              color:l.color,
              marginBottom:l.isName?22:8,
              minHeight:l.size*1.5,
              opacity:visible?1:0,
              transform:visible?"translateY(0)":"translateY(8px)",
              transition:"opacity 0.5s ease, transform 0.5s ease",
              textShadow:l.isPremiumName
                ?"none"
                :l.color===SYS?`0 0 20px ${SYS},0 0 40px ${SYS}`:"none",
              wordBreak:"break-word",
              lineHeight:1.25,
            }}>
              {l.isPremiumName?(
                <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
                  <GoldenDagger size={Math.max(12,nameSize(name)*0.55)} level={level||1}/>
                  <span style={{
                    background:"linear-gradient(90deg,#92400e,#fbbf24,#fef3c7,#fbbf24,#d97706,#fbbf24,#92400e)",
                    backgroundSize:"200% auto",
                    WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",
                    animation:"goldShimmer 3s linear infinite"}}>
                    {displayTxt}
                  </span>
                </span>
              ):displayTxt}
              {isTyping&&(
                <span style={{display:"inline-block",
                  animation:"blink 0.5s step-end infinite",
                  color:l.color,marginLeft:1}}>▌</span>
              )}
            </div>
          );
        })}
        {tapReady&&(
          <div style={{marginTop:32,fontSize:10,letterSpacing:3,
            color:"rgba(56,189,248,0.4)",
            fontFamily:"'Share Tech Mono',monospace",
            animation:"blink 1.2s infinite"}}>
            [ TAP TO ENTER ]
          </div>
        )}
      </div>
    </div>
  );
}

// ─── System Notification ──────────────────────────────────────────────────────
// ─── Demon King Sword (Solo Leveling premium prefix) ─────────────────────────
function getSwordForLevel(level){
  if(level <= 10) return "/swords/sword_1.png";
  if(level <= 20) return "/swords/sword_2.png";
  if(level <= 30) return "/swords/sword_3.png";
  if(level <= 40) return "/swords/sword_4.png";
  if(level <= 50) return "/swords/sword_5.png";
  if(level <= 60) return "/swords/sword_6.png";
  if(level <= 70) return "/swords/sword_7.png";
  if(level <= 80) return "/swords/sword_8.png";
  return "/swords/sword_9.png";
}

function GoldenDagger({size=28, level=1}){
  return(
    <img
      src={getSwordForLevel(level)}
      alt="sword"
      style={{
        width:size,
        height:size,
        objectFit:"contain",
        display:"inline-block",
        verticalAlign:"middle",
        flexShrink:0,
        filter:"drop-shadow(0 0 6px rgba(220,50,50,0.7)) drop-shadow(0 0 12px rgba(251,191,36,0.4))",
        marginRight:2,
      }}
    />
  );
}

function SysNotif({msg,sub,type,anim}){
  const color=type==="warn"?WARN:type==="success"?"#4ade80":SYS;
  const isLine = anim==="line";
  return(
    <div style={{position:"fixed",top:"50%",left:"50%",transform:"translateX(-50%) translateY(-50%)",
  zIndex:8888,pointerEvents:"none",width:"90%",maxWidth:400}}>
      <div style={{position:"relative"}}>
        {isLine&&(
          <div style={{position:"absolute",top:0,left:"50%",width:2,height:"100%",
            background:color,boxShadow:`0 0 10px ${color}, 0 0 22px ${color}`,
            transform:"translateX(-50%)",borderRadius:2,zIndex:1,
            animation:"lineGlow 0.5s ease forwards"}}/>
        )}
        <div style={{background:"rgba(2,6,14,0.97)",border:`1px solid ${color}55`,
          borderLeft:`3px solid ${color}`,borderRadius:10,padding:"11px 16px",position:"relative",zIndex:2,
          boxShadow:`0 0 30px ${color}22, 0 8px 32px rgba(0,0,0,0.8)`,
          animation:isLine?"lineReveal 0.5s cubic-bezier(0.4,0,0.2,1) forwards":"notifIn 0.35s cubic-bezier(0.34,1.56,0.64,1)"}}>
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,letterSpacing:2,color,marginBottom:2}}>
            [ SYSTEM {type==="warn"?"WARNING":type==="success"?"NOTICE":"UPDATE"} ]
          </div>
          <div style={{fontSize:13,fontWeight:700,color:"#e2e2f0"}}>{msg}</div>
          {sub&&<div style={{fontSize:11,color:"#94a3b8",marginTop:2}}>{sub}</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Metric Input ─────────────────────────────────────────────────────────────
function MetricInput({metric,customUnit,value,onChange,compact}){
  const fs=compact?13:14;
  if(metric==="time"){
    return(
      <div style={{display:"flex",gap:14}}>
        <div style={{flex:1,position:"relative"}}>
          <input className="field" type="number" min="0" placeholder="0" value={value.h||""} onChange={e=>onChange({...value,h:e.target.value})} style={{paddingRight:34,fontSize:fs}}/>
          <span style={{position:"absolute",right:11,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"#64748b"}}>hr</span>
        </div>
        <div style={{flex:1,position:"relative"}}>
          <input className="field" type="number" min="0" max="59" placeholder="0" value={value.m||""} onChange={e=>onChange({...value,m:e.target.value})} style={{paddingRight:34,fontSize:fs}}/>
          <span style={{position:"absolute",right:11,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"#64748b"}}>min</span>
        </div>
      </div>
    );
  }
  const unit=metric==="custom"?(customUnit||"units"):METRICS[metric]?.unit;
  return(
    <div style={{position:"relative"}}>
      <input className="field" type="number" min="0" step={METRICS[metric]?.isDecimal?"0.1":"1"} placeholder="0"
        value={value.amount||""} onChange={e=>onChange({...value,amount:e.target.value})}
        style={{paddingRight:unit?unit.length*9+16:14,fontSize:fs}}/>
      {unit&&<span style={{position:"absolute",right:11,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"#64748b"}}>{unit}</span>}
    </div>
  );
}

// ─── Metric Picker ────────────────────────────────────────────────────────────
function MetricPicker({value,customUnit,onChangeMetric,onChangeUnit}){
  return(
    <div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:value==="custom"?8:0}}>
        {Object.entries(METRICS).map(([id,m])=>(
          <button key={id} className="btn sys-btn" onClick={()=>onChangeMetric(id)}
            style={{padding:"5px 10px",fontSize:12,
              background:value===id?SYS_DIM:"rgba(255,255,255,0.03)",
              color:value===id?SYS:"#64748b",
              border:`1px solid ${value===id?SYS_BORDER:"rgba(255,255,255,0.06)"}`}}>
            {m.icon} {m.label}
          </button>
        ))}
      </div>
      {value==="custom"&&(
        <input className="field" placeholder="Unit name (e.g. pushups, sessions...)" value={customUnit||""}
          onChange={e=>onChangeUnit(e.target.value)} style={{marginTop:8,fontSize:13}}/>
      )}
    </div>
  );
}

// ─── Goal Editor ─────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function GoalEditor({task,onSave,onClose}){
  const [goals,setGoals]=useState(task.goals||{});
  const set=(p,v)=>setGoals(prev=>({...prev,[p]:v}));
  const clear=p=>setGoals(prev=>{const n={...prev};delete n[p];return n;});
  const hasG=(p)=>{const g=goals[p];return!!g&&(task.metric==="time"?(parseInt(g.h||0)+parseInt(g.m||0))>0:parseFloat(g.amount||0)>0);};
  return(
    <div className="sys-panel" style={{marginBottom:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div className="sys-label">[ TARGET PARAMETERS ]</div>
          <div style={{fontSize:13,fontWeight:700,color:"#fff",marginTop:3}}>{METRICS[task.metric]?.icon} {METRICS[task.metric]?.label}</div>
          <div style={{fontSize:11,color:"#64748b",marginTop:2}}>Set one — others auto-calculate</div>
        </div>
        <button onClick={()=>{AudioEngine.close();onClose();}} className="sys-close">✕</button>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:9}}>
        {PERIODS.map(p=>{
          const g=goals[p];
          const active=hasG(p);
          const previewTask={...task,goals};
          const derived=active?0:getEffectiveGoalAmt(previewTask,p);
          const source=getGoalSource(previewTask,p);
          return(
            <div key={p} className={active?"sys-row-active":"sys-row"}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:active?10:0}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:12,fontWeight:700,color:active?SYS:"#64748b",textTransform:"uppercase",letterSpacing:2}}>{p}</span>
                  {!active&&derived>0&&(
                    <span style={{fontSize:10,color:"#64748b",fontFamily:"monospace"}}>→ {fmtAmount(derived,task.metric,task.customUnit)} <span style={{color:"#64748b"}}>from {source}</span></span>
                  )}
                  {active&&<span className="sys-tag">SET</span>}
                </div>
                {!active
                  ?<button className="btn sys-btn-sm" onClick={()=>set(p,task.metric==="time"?{h:"",m:""}:{amount:""})}>+ Set</button>
                  :<button className="btn sys-btn-sm warn" onClick={()=>{AudioEngine.click();clear(p)}}>Remove</button>
                }
              </div>
              {g!==undefined&&<MetricInput metric={task.metric} customUnit={task.customUnit} value={g||{}} onChange={v=>set(p,v)} compact/>}
            </div>
          );
        })}
      </div>
      <button className="btn sys-btn-primary" onClick={()=>{AudioEngine.click();onSave(goals)}} style={{marginTop:14,width:"100%",padding:"12px"}}>
        [ CONFIRM PARAMETERS ]
      </button>
    </div>
  );
}

// ─── Dashboard Row ────────────────────────────────────────────────────────────
function DashRow({task,period,onClick}){
  const {achieved,goalAmt,pct,remaining,overage,hasGoal,met,behind,isDerived,source}=getPeriodProgress(task,period);
  if(!hasGoal)return null;
  const cfg=CAT[task.cat];
  return(
    <div onClick={onClick} style={{cursor:"pointer",padding:"10px 13px",borderRadius:8,marginBottom:10,
      background:met?"rgba(74,222,128,0.04)":behind?"rgba(239,68,68,0.04)":"rgba(56,189,248,0.03)",
      border:`1px solid ${met?"rgba(74,222,128,0.2)":behind?"rgba(239,68,68,0.15)":SYS_BORDER}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
        <div style={{display:"flex",alignItems:"center",gap:7}}>
          <span style={{fontSize:11}}>{cfg.icon}</span>
          <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,color:"#aac"}}>{task.title}</span>
          {isDerived&&<span style={{fontSize:9,color:"#64748b",fontFamily:"monospace"}}>from {source}</span>}
        </div>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,fontWeight:700}}>
          <span style={{color:met?"#4ade80":behind?WARN:SYS}}>{fmtAmount(achieved,task.metric,task.customUnit)}</span>
          <span style={{color:"#64748b"}}> / {fmtAmount(goalAmt,task.metric,task.customUnit)}</span>
        </div>
      </div>
      <div style={{height:3,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden",marginBottom:4}}>
        <div style={{height:"100%",borderRadius:2,transition:"width .5s ease",
          background:met?"linear-gradient(90deg,#4ade80,#22d3ee)":behind?`linear-gradient(90deg,${WARN},#f97316)`:`linear-gradient(90deg,${SYS},${SYS2})`,
          width:`${pct||0}%`}}/>
      </div>
      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:met?"#4ade80":behind?WARN:"#64748b"}}>
        {met
          ? overage>0?`+${fmtAmount(overage,task.metric,task.customUnit)} EXCEEDED`:"COMPLETE"
          : behind?`⚠ ${fmtAmount(remaining,task.metric,task.customUnit)} REMAINING — BEHIND PACE`
          :`${fmtAmount(remaining,task.metric,task.customUnit)} remaining`
        }
      </div>
    </div>
  );
}

// ─── Metric Change Modal ──────────────────────────────────────────────────────
function MetricChangeModal({task,newMetric,onKeep,onReset,onCancel}){
  return(
    <div style={{position:"fixed",inset:0,background:"#000",zIndex:8600,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div className="sys-panel" style={{maxWidth:380,width:"100%"}}>
        <div className="sys-label" style={{marginBottom:8}}>[ SYSTEM OVERRIDE ]</div>
        <h3 style={{fontSize:16,fontWeight:800,color:"#fff",marginBottom:8}}>Change Tracking Metric?</h3>
        <p style={{fontSize:13,color:"#64748b",lineHeight:1.6,marginBottom:20}}>
          Target has <strong style={{color:"#aaa"}}>{task.logs.length} report{task.logs.length!==1?"s":""}</strong> in <strong style={{color:"#aaa"}}>{METRICS[task.metric]?.label}</strong>.
          Switching to <strong style={{color:SYS}}>{METRICS[newMetric]?.label}</strong>.
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <button className="btn sys-btn-block" onClick={onKeep}>
            <div style={{fontWeight:700,marginBottom:2}}>Preserve old reports</div>
            <div style={{fontSize:11,color:"#64748b"}}>Old reports stay. New reports use {METRICS[newMetric]?.label}.</div>
          </button>
          <button className="btn sys-btn-block warn" onClick={onReset}>
            <div style={{fontWeight:700,marginBottom:2}}>Purge all reports</div>
            <div style={{fontSize:11,color:"#64748b"}}>Start fresh with {METRICS[newMetric]?.label}.</div>
          </button>
          <button className="btn" onClick={onCancel} style={{padding:"10px",background:"none",color:"#64748b",border:`1px solid rgba(255,255,255,0.06)`,fontSize:13}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Card Scroller (horizontal zoom on active card) ──────────────────────────
function CardScroller({filtered,cardPeriod,openTask,quickLog}){
  const scrollRef=useRef(null);
  const [activeIdx,setActiveIdx]=useState(0);

  function onScroll(){
    const el=scrollRef.current;
    if(!el)return;
    const cards=[...el.children];
    const containerCenter=el.getBoundingClientRect().left+el.offsetWidth/2;
    let closest=0,closestDist=Infinity;
    cards.forEach((card,i)=>{
      const rect=card.getBoundingClientRect();
      const cardCenter=rect.left+rect.width/2;
      const dist=Math.abs(containerCenter-cardCenter);
      if(dist<closestDist){closestDist=dist;closest=i;}
    });
    setActiveIdx(closest);
  }

  useEffect(()=>{
    const el=scrollRef.current;
    if(!el)return;
    onScroll(); // run on mount so initial active card is correct
    el.addEventListener("scroll",onScroll,{passive:true});
    return()=>el.removeEventListener("scroll",onScroll);
  },[filtered]);

  if(filtered.length===0)return(
    <div style={{textAlign:"center",padding:"50px 0"}}>
      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,letterSpacing:3,color:"rgba(56,189,248,0.4)"}}>[ NO TARGETS FOUND ]</div>
    </div>
  );

  return(
    <div ref={scrollRef} style={{
      display:"flex",flexDirection:"row",gap:14,
      overflowX:"auto",paddingBottom:20,paddingTop:8,
      paddingLeft:18,paddingRight:18,
      scrollSnapType:"x mandatory",
      WebkitOverflowScrolling:"touch",
      msOverflowStyle:"none",scrollbarWidth:"none",
      margin:"0 -18px",
    }}>
      {filtered.map((t,idx)=>{
        const cfg=CAT[t.cat];
        const {achieved,goalAmt,pct,remaining,overage,hasGoal,met,behind}=getPeriodProgress(t,cardPeriod);
        const hasAnyGoal=PERIODS.some(p=>getEffectiveGoalAmt(t,p)>0);
        const isActive=idx===activeIdx;
        return(
          <div key={t.id} className="card-target"
            onClick={()=>{if(isActive){AudioEngine.open();openTask(t);}else{scrollRef.current?.children[idx]?.scrollIntoView({behavior:"smooth",inline:"center",block:"nearest"});}}}
            style={{
              background:"rgba(4,12,28,0.9)",
              border:`1px solid ${met?"rgba(74,222,128,0.2)":behind?"rgba(239,68,68,0.15)":isActive?"rgba(56,189,248,0.35)":"rgba(56,189,248,0.12)"}`,
              borderRadius:14,overflow:"hidden",
              minWidth:272,maxWidth:272,flexShrink:0,
              scrollSnapAlign:"center",
              cursor:isActive?"pointer":"default",
              transform:isActive?"scale(1.06)":"scale(0.93)",
              opacity:isActive?1:0.55,
              boxShadow:isActive?`0 16px 48px rgba(56,189,248,0.15),0 0 0 1px rgba(56,189,248,0.2)`:"none",
              transition:"transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94),opacity 0.4s ease,box-shadow 0.4s ease,border-color 0.4s ease",
              willChange:"transform,opacity",
            }}>
            <div style={{height:2,background:`linear-gradient(90deg,${cfg.color},transparent)`}}/>
            <div style={{padding:"16px 16px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div style={{flex:1,cursor:"pointer"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                    <span style={{fontSize:10,color:cfg.color,fontFamily:"monospace",letterSpacing:2,textTransform:"uppercase"}}>{cfg.icon} {cfg.label}</span>
                    <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                      color:SCOL[t.status],background:`${SCOL[t.status]}15`,
                      padding:"1px 7px",borderRadius:4,border:`1px solid ${SCOL[t.status]}30`}}>
                      {t.status.toUpperCase()}
                    </span>
                    {behind&&<span style={{fontSize:9,color:WARN,fontFamily:"monospace",letterSpacing:1}}>⚠ BEHIND</span>}
                  </div>
                  <h3 style={{fontSize:15,fontWeight:700,color:"#ddeeff",marginBottom:3,letterSpacing:.3}}>{t.title}</h3>
                  <p style={{fontSize:12,color:"#64748b",lineHeight:1.4}}>{t.desc||"No description."}</p>
                </div>
                <button className="quick" onClick={e=>{e.stopPropagation();AudioEngine.log();quickLog(e,t)}}
                  style={{width:38,height:38,marginLeft:10,flexShrink:0,
                    background:`linear-gradient(135deg,${cfg.color}15,${cfg.color}08)`,
                    border:`1px solid ${cfg.color}44`,color:cfg.color}}>
                  +
                </button>
              </div>
              {(hasGoal||hasAnyGoal)&&(
                <div style={{marginTop:4}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,fontFamily:"'Share Tech Mono',monospace",fontSize:10}}>
                    <span style={{color:"#64748b"}}>{PERIOD_LABELS[cardPeriod].toUpperCase()}</span>
                    <span style={{color:met?"#4ade80":behind?WARN:SYS}}>
                      {fmtAmount(achieved,t.metric,t.customUnit)} / {fmtAmount(goalAmt,t.metric,t.customUnit)}
                    </span>
                  </div>
                  <div className="ptrack">
                    <div className="pfill" style={{width:`${pct||0}%`,
                      background:met?"linear-gradient(90deg,#4ade80,#22d3ee)":behind?`linear-gradient(90deg,${WARN},#f97316)`:`linear-gradient(90deg,${SYS},${SYS2})`}}/>
                  </div>
                  {(()=>{
                    const hasIP=logsInPeriod(t.logs,cardPeriod,t.metric).some(l=>l.status==="In Progress");
                    if(hasIP) return(
                      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,color:"#4ade80",marginTop:4}}>
                        ● SESSION IN PROGRESS
                      </div>
                    );
                    return(
                      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,color:met?"#4ade80":behind?WARN:"#64748b",marginTop:4}}>
                        {met
                          ? overage>0?`+${fmtAmount(overage,t.metric,t.customUnit)} EXCEEDED`:"COMPLETE"
                          : behind?`⚠ ${fmtAmount(remaining,t.metric,t.customUnit)} REMAINING`
                          :`${fmtAmount(remaining,t.metric,t.customUnit)} remaining`
                        }
                      </div>
                    );
                  })()}
                </div>
              )}
              {!hasAnyGoal&&(
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:6}}>
                  {METRICS[t.metric]?.icon} {fmtAmount(t.total,t.metric,t.customUnit)} total · {t.logs.length} report{t.logs.length!==1?"s":""}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Edit Target Modal ────────────────────────────────────────────────────────
function EditTargetModal({task,onSave,onClose}){
  const [title,setTitle]         = useState(task.title);
  const [cat,setCat]             = useState(task.cat);
  const [metric,setMetric]       = useState(task.metric);
  const [customUnit,setCustomUnit] = useState(task.customUnit||"");
  const [goals,setGoals]         = useState(task.goals||{});// preloaded from task
  const setGoal=(p,v)=>setGoals(prev=>({...prev,[p]:v}));
  const clearGoal=p=>setGoals(prev=>{const n={...prev};delete n[p];return n;});
  const hasG=p=>goals[p]!==undefined;

  // When metric changes, reset goals since units are incompatible
  function handleMetricChange(m){
    setMetric(m);
    setCustomUnit("");
    setGoals({});
  }

  function save(){
    if(!title.trim())return;
    onSave(title.trim(), cat, metric, customUnit, goals);
  }

  return(
    <div style={{position:"fixed",inset:0,background:"#000",zIndex:8700,
      display:"flex",alignItems:"flex-end",justifyContent:"center",paddingBottom:90}}>
      <div style={{background:"#030912",border:"1px solid rgba(56,189,248,0.25)",
        borderRadius:"18px 18px 0 0",width:"100%",maxWidth:560,maxHeight:"calc(92vh - 90px)",
        overflowY:"auto",padding:"22px 18px 40px",
        animation:"slideUp 0.3s cubic-bezier(.34,1.56,.64,1)"}}>

        {/* Header */}
        <div style={{height:2,background:"linear-gradient(90deg,rgba(56,189,248,0.8),transparent)",borderRadius:2,marginBottom:18}}/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
          <div>
            <div className="sys-label" style={{marginBottom:4}}>[ EDIT TARGET ]</div>
            <div style={{fontSize:11,color:"#64748b"}}>All changes update progress immediately</div>
          </div>
          <button onClick={()=>{AudioEngine.close();onClose();}} className="sys-close" style={{fontSize:18}}>✕</button>
        </div>

        {/* Title */}
        <div style={{marginBottom:22}}>
          <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:8}}>TARGET NAME</div>
          <input className="field" value={title} onChange={e=>setTitle(e.target.value)}
            style={{fontSize:15,fontWeight:700}} autoFocus
            onKeyDown={e=>e.key==="Enter"&&save()}/>
        </div>

        {/* Category */}
        <div style={{marginBottom:22}}>
          <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:8}}>CATEGORY</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {Object.entries(CAT).map(([id,cfg])=>(
              <button key={id} className="btn" onClick={()=>{AudioEngine.click();setCat(id)}}
                style={{padding:"6px 12px",fontSize:11,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,
                  background:cat===id?`${cfg.color}18`:"rgba(255,255,255,0.02)",
                  color:cat===id?cfg.color:"#64748b",
                  border:`1px solid ${cat===id?cfg.color+"44":"rgba(255,255,255,0.06)"}`}}>
                {cfg.icon} {cfg.label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Metric */}
        <div style={{marginBottom:22}}>
          <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:8}}>WHAT TO TRACK</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {Object.entries(METRICS).map(([id,m])=>(
              <button key={id} className="btn sys-btn" onClick={()=>handleMetricChange(id)}
                style={{padding:"6px 11px",fontSize:11,
                  background:metric===id?SYS_DIM:"rgba(255,255,255,0.02)",
                  color:metric===id?SYS:"#64748b",
                  border:`1px solid ${metric===id?SYS_BORDER:"rgba(255,255,255,0.06)"}`}}>
                {m.icon} {m.label}
              </button>
            ))}
          </div>
          {metric==="custom"&&(
            <input className="field" placeholder="Unit name (e.g. sessions, pushups...)"
              value={customUnit} onChange={e=>setCustomUnit(e.target.value)}
              style={{marginTop:10,fontSize:13}}/>
          )}
          {metric!==task.metric&&(
            <div style={{marginTop:8,fontFamily:"monospace",fontSize:10,color:WARN}}>
              ⚠ Metric changed — existing reports stay, goals reset
            </div>
          )}
        </div>

        {/* Goals */}
        <div style={{marginBottom:22}}>
          <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:8}}>TARGET PARAMETERS</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {PERIODS.map(p=>{
              const g=goals[p];
              const isSet=hasG(p);
              const isFilled=!!g&&(metric==="time"?(parseInt(g.h||0)+parseInt(g.m||0))>0:parseFloat(g.amount||0)>0);
              const previewTask={metric,customUnit,goals,logs:[]};
              const derived=isSet?0:getEffectiveGoalAmt(previewTask,p);
              const src=getGoalSource(previewTask,p);
              return(
                <div key={p} className={isSet?"sys-row-active":"sys-row"}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isSet?10:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,letterSpacing:2,
                        color:isSet?SYS:"#64748b",textTransform:"uppercase"}}>{p}</span>
                      {isFilled&&<span className="sys-tag">SET</span>}
                      {!isSet&&derived>0&&(
                        <span style={{fontSize:10,color:"#64748b",fontFamily:"monospace"}}>
                          → {fmtAmount(derived,metric,customUnit)} <span style={{color:"#64748b"}}>from {src}</span>
                        </span>
                      )}
                    </div>
                    {!isSet
                      ?<button className="btn sys-btn-sm" onClick={()=>setGoal(p,metric==="time"?{h:"",m:""}:{amount:""})}>+ Set</button>
                      :<button className="btn sys-btn-sm warn" onClick={()=>clearGoal(p)}>Remove</button>
                    }
                  </div>
                  {isSet&&(
                    <MetricInput metric={metric} customUnit={customUnit}
                      value={g||{}} onChange={v=>setGoal(p,v)} compact/>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Save */}
        <button className="btn sys-btn-primary" onClick={save}
          style={{width:"100%",padding:"14px",fontSize:12}}>
          [ SAVE CHANGES ]
        </button>
      </div>
    </div>
  );
}

// ─── XP / Level System ───────────────────────────────────────────────────────
const DEFAULT_XP = 10;

// XP per log = (log value / weekly goal) × weekly XP
// e.g. Log 10 pages, weekly goal 70 pages, XP=10 → (10/70)×10 = 1.43 XP
function xpForLog(task, log){
  if(log.status==="In Progress"||log.status==="Not Started") return 0;
  const weeklyXP = task.xp || DEFAULT_XP;
  const goal = getEffectiveGoalAmt(task, "weekly");
  if(!goal || goal <= 0) return weeklyXP; // no goal → full XP per log
  const metric = task.metric;
  let value = 0;
  if(metric === "time") value = log.value?.totalMins || 0;
  else value = parseFloat(log.value?.amount || 0);
  if(value <= 0) return 0;
  return (value / goal) * weeklyXP;
}

// Keep xpPerLog for backwards compat (used in display estimates)
// eslint-disable-next-line no-unused-vars
function xpPerLog(task){
  const weeklyXP = task.xp || DEFAULT_XP;
  const goal = getEffectiveGoalAmt(task, "weekly");
  if(!goal || goal <= 0) return weeklyXP;
  // Average XP assuming logs are evenly distributed across goal
  return weeklyXP / Math.max(1, goal);
}

// Total XP = sum of proportional XP for every log
function calcTotalXP(tasks){
  return tasks.reduce((sum, t) =>
    sum + (t.logs || []).reduce((s, log) => s + xpForLog(t, log), 0)
  , 0);
}

// Lv1→2 threshold = sum of all weekly XP values set
function calcL1Threshold(tasks){
  return Math.max(1,tasks.reduce((s,t)=>s+(t.xp||DEFAULT_XP),0));
}

// Fixed: each level costs exactly 1 week of hitting all goals (base XP)
function xpToLevel(totalXP,base){
  if(!base||base<=0)return{level:1,xpIntoLevel:0,xpForNext:100,pct:0};
  const level=Math.floor(totalXP/base)+1;
  const accumulated=(level-1)*base;
  const xpIntoLevel=Math.floor(totalXP-accumulated);
  const xpForNext=Math.round(base);
  const pct=Math.min(100,Math.round((xpIntoLevel/xpForNext)*100));
  return{level,xpIntoLevel,xpForNext,pct};
}

// eslint-disable-next-line no-unused-vars
function getWeekKey(){
  const d=new Date();
  const jan1=new Date(d.getFullYear(),0,1);
  const week=Math.ceil(((d-jan1)/86400000+jan1.getDay()+1)/7);
  return`${d.getFullYear()}-W${String(week).padStart(2,"0")}`;
}

// ─── Level Modal ──────────────────────────────────────────────────────────────
function LevelModal({tasks,onUpdateXP,onClose}){
  const [xpMap,setXpMap]=useState(()=>Object.fromEntries(tasks.map(t=>[t.id,String(t.xp||DEFAULT_XP)])));
  const previewTasks=tasks.map(t=>({...t,xp:parseInt(xpMap[t.id])||DEFAULT_XP}));
  const totalXP=calcTotalXP(previewTasks);
  const base=calcL1Threshold(previewTasks);
  const {level,xpIntoLevel,xpForNext,pct}=xpToLevel(totalXP,base);

  return(
    <div style={{position:"fixed",inset:0,background:"#000",zIndex:8700,
      display:"flex",alignItems:"flex-end",justifyContent:"center",paddingBottom:90}}>
      <div style={{background:"#030912",border:"1px solid rgba(56,189,248,0.25)",
        borderRadius:"18px 18px 0 0",width:"100%",maxWidth:560,maxHeight:"calc(92vh - 90px)",
        overflowY:"auto",padding:"22px 18px 40px",
        animation:"slideUp 0.3s cubic-bezier(.34,1.56,.64,1)"}}>

        <div style={{height:2,background:"linear-gradient(90deg,rgba(56,189,248,0.8),transparent)",borderRadius:2,marginBottom:18}}/>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
          <div>
            <div className="sys-label" style={{marginBottom:4}}>[ PLAYER PROFILE ]</div>
            <div style={{fontSize:11,color:"#64748b"}}>Each log earns XP · weekly XP ÷ goal = XP per log</div>
          </div>
          <button onClick={()=>{AudioEngine.close();onClose();}} className="sys-close" style={{fontSize:18}}>✕</button>
        </div>

        {/* Level card */}
        <div style={{background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,borderRadius:12,padding:"16px 18px",marginBottom:22}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div>
              <div className="sys-label" style={{marginBottom:4}}>CURRENT LEVEL</div>
              <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:36,fontWeight:800,
                color:SYS,textShadow:`0 0 24px ${SYS}`,lineHeight:1}}>{level}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:2}}>TOTAL XP</div>
              <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:20,fontWeight:700,color:"#fff"}}>{Math.floor(totalXP)}</div>
              <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",marginTop:4}}>Lv1→2: {base} XP</div>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:6}}>
            <span>{xpIntoLevel} / {xpForNext} XP → Level {level+1}</span>
            <span>{pct}%</span>
          </div>
          <div style={{height:4,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden"}}>
            <div style={{height:"100%",borderRadius:2,width:`${pct}%`,
              background:"linear-gradient(90deg,#38bdf8,#818cf8)",transition:"width 0.5s ease"}}/>
          </div>
          <div style={{marginTop:10,fontFamily:"monospace",fontSize:9,color:"#64748b",lineHeight:1.6}}>
            Lv{level}→{level+1}: {xpForNext} XP &nbsp;·&nbsp; Lv{level+1}→{level+2}: {xpForNext} XP &nbsp;·&nbsp; Lv{level+2}→{level+3}: {xpForNext} XP
          </div>
        </div>

        {/* Per task XP */}
        <div style={{marginBottom:8}}>
          <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:6}}>[ XP ALLOCATION ]</div>
          <div style={{padding:"10px 14px",background:"rgba(56,189,248,0.04)",border:`1px solid ${SYS_BORDER}`,
            borderRadius:10,marginBottom:14,fontFamily:"monospace",fontSize:10,color:"#64748b",lineHeight:1.8}}>
            <span style={{color:SYS,fontWeight:700}}>How XP works:</span> Each target gets a weekly XP budget.
            Completing your weekly goal earns the full amount. Each log earns
            <span style={{color:"#aaa"}}> weekly XP ÷ sessions needed</span>.
            The total across all targets sets your <span style={{color:SYS}}>Lv 1→2 threshold</span>.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {tasks.map(t=>{
              const cfg=CAT[t.cat];
              const weeklyXP=parseInt(xpMap[t.id])||DEFAULT_XP;
              const totalAlloc=Object.values(xpMap).reduce((s,v)=>s+(parseInt(v)||0),0)||1;
              const sharePct=Math.round((weeklyXP/totalAlloc)*10000)/100;
              const goal=getEffectiveGoalAmt(t,"weekly");
              // Earned XP = sum of proportional XP for each log
              const earned=Math.floor((t.logs||[]).reduce((s,log)=>s+xpForLog({...t,xp:weeklyXP},log),0));
              // Example XP per log — show what 1 unit of goal earns
              const perLog=goal>0?Math.round((weeklyXP/goal)*100)/100:weeklyXP;
              return(
                <div key={t.id} style={{background:"rgba(255,255,255,0.02)",
                  border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,padding:"10px 14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:4}}>
                        <span style={{fontSize:12}}>{cfg.icon}</span>
                        <span style={{fontWeight:700,color:"#ddeeff",fontSize:13}}>{t.title}</span>
                        <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,
                          color:"#4ade80",fontWeight:700,marginLeft:2}}>{sharePct}%</span>
                      </div>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",lineHeight:1.7}}>
                        {perLog} XP/unit · {(t.logs||[]).length} logs · <span style={{color:SYS}}>{earned} XP earned</span>
                        {goal>0&&<><br/><span style={{color:"#64748b"}}>weekly goal: {goal} → full {weeklyXP} XP when met</span></>}
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                      <button onClick={()=>{AudioEngine.click();setXpMap(prev=>({...prev,[t.id]:String(Math.max(5,(parseInt(prev[t.id])||DEFAULT_XP)-5))}));}}
                        style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",
                          color:"#667",borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:14,fontWeight:700}}>−</button>
                      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:16,fontWeight:800,
                        color:SYS,minWidth:36,textAlign:"center"}}>{weeklyXP}</div>
                      <button onClick={()=>{AudioEngine.click();setXpMap(prev=>({...prev,[t.id]:String((parseInt(prev[t.id])||DEFAULT_XP)+5)}));}}
                        style={{background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,
                          color:SYS,borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:14,fontWeight:700}}>+</button>
                    </div>
                  </div>
                  {/* XP share bar */}
                  <div style={{marginTop:8,height:3,background:"rgba(255,255,255,0.04)",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${sharePct}%`,borderRadius:2,
                      background:"linear-gradient(90deg,#4ade80,#86efac)",transition:"width 0.4s ease"}}/>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{marginTop:12,padding:"10px 14px",background:"rgba(56,189,248,0.05)",
            border:`1px solid ${SYS_BORDER}`,borderRadius:10,
            fontFamily:"monospace",fontSize:10,color:"#64748b",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>Total weekly XP pool</span>
            <span style={{color:SYS,fontWeight:700}}>{Object.values(xpMap).reduce((s,v)=>s+(parseInt(v)||0),0)} XP</span>
          </div>
        </div>

        <button className="btn sys-btn-primary" onClick={()=>{AudioEngine.click();onUpdateXP(xpMap)}}
          style={{width:"100%",padding:"14px",fontSize:12,marginTop:14}}>
          [ SAVE XP VALUES ]
        </button>
      </div>
    </div>
  );
}
// ─── Level Up Screen ──────────────────────────────────────────────────────────
function LevelUpScreen({fromLevel, toLevel, onContinue}){
  return(
    <div style={{position:"fixed",inset:0,zIndex:9999,display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",
      background:"rgba(2,6,14,0.97)"}}>
      <div style={{position:"relative",maxWidth:340,width:"90%"}}>
        <div style={{position:"absolute",top:0,left:"50%",width:3,height:"100%",
          background:"#38bdf8",boxShadow:"0 0 14px #38bdf8, 0 0 28px #38bdf8",
          transform:"translateX(-50%)",borderRadius:2,zIndex:1,
          animation:"lineGlow 0.6s ease forwards"}}/>
        <div style={{background:"rgba(56,189,248,0.07)",border:"1px solid rgba(56,189,248,0.25)",
          borderRadius:20,padding:"48px 40px",textAlign:"center",width:"100%",position:"relative",zIndex:2,
          boxShadow:"0 0 60px rgba(56,189,248,0.15), 0 0 120px rgba(56,189,248,0.07)",
          animation:"lineReveal 0.6s cubic-bezier(0.4,0,0.2,1) forwards"}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,letterSpacing:4,
          color:"#38bdf8",marginBottom:24}}>[ SYSTEM NOTICE ]</div>
        <div style={{fontSize:48,fontWeight:900,letterSpacing:2,color:"#fff",
          textShadow:"0 0 30px rgba(56,189,248,0.8), 0 0 60px rgba(56,189,248,0.4)",
          marginBottom:16,fontFamily:"'Share Tech Mono',monospace"}}>
          LEVEL UP
        </div>
        <div style={{fontSize:22,fontWeight:700,color:"#38bdf8",
          fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,marginBottom:40}}>
          Lv.{fromLevel} → Lv.{toLevel}
        </div>
        <button onClick={onContinue} style={{
          background:"rgba(56,189,248,0.12)",border:"1px solid rgba(56,189,248,0.4)",
          borderRadius:10,padding:"14px 32px",color:"#38bdf8",
          fontFamily:"'Share Tech Mono',monospace",fontSize:13,letterSpacing:2,
          cursor:"pointer",display:"flex",alignItems:"center",gap:10,margin:"0 auto"}}>
          <span style={{fontSize:16}}>✓</span> CONTINUE
        </button>
        </div>
      </div>
    </div>
  );
}


// ─── App ──────────────────────────────────────────────────────────────────────


// ─── Intro / How to Use Screen (shown every open until logged in) ─────────────
function IntroScreen({ onEnter }) {
  return (
    <div style={{position:"fixed",inset:0,background:"#020810",overflowY:"auto",
      fontFamily:"'Outfit','DM Sans',sans-serif"}}>
      <style>{`
        @keyframes intro_fadein{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes intro_glow{0%,100%{opacity:0.5}50%{opacity:1}}
        @keyframes intro_pulse{0%,100%{box-shadow:0 0 0px #38bdf8}50%{box-shadow:0 0 18px #38bdf8}}
        .intro-card{background:rgba(56,189,248,0.04);border:1px solid rgba(56,189,248,0.15);
          border-radius:14px;padding:18px 16px;margin-bottom:12px;
          animation:intro_fadein 0.6s ease both}
        .intro-enter-btn{
          width:100%;padding:16px;background:rgba(56,189,248,0.12);
          border:1px solid #38bdf8;border-radius:12px;cursor:pointer;
          font-family:'Share Tech Mono',monospace;font-size:14px;font-weight:700;
          color:#38bdf8;letter-spacing:3px;text-transform:uppercase;
          animation:intro_pulse 2s ease infinite;
          transition:background 0.2s}
        .intro-enter-btn:active{background:rgba(56,189,248,0.25)}
      `}</style>

      <div style={{maxWidth:480,margin:"0 auto",padding:"48px 20px 48px"}}>

        {/* Logo + Title */}
        <div style={{textAlign:"center",marginBottom:40,animation:"intro_fadein 0.5s ease both"}}>
          <img src="/icons/playstore_512.png" alt="ARISE"
            style={{width:72,height:72,borderRadius:"50%",marginBottom:16,
              boxShadow:"0 0 24px rgba(56,189,248,0.4)"}}/>
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:28,fontWeight:900,
            color:"#38bdf8",letterSpacing:6,marginBottom:6}}>ARISE</div>
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
            color:"rgba(56,189,248,0.6)",letterSpacing:4}}>LEVEL UP YOUR REAL LIFE</div>
          <div style={{width:60,height:1,background:"linear-gradient(90deg,transparent,#38bdf8,transparent)",
            margin:"20px auto 0"}}/>
        </div>

        {/* Hook */}
        <div style={{textAlign:"center",marginBottom:32,animation:"intro_fadein 0.6s 0.1s ease both",opacity:0}}>
          <p style={{fontSize:16,color:"#c8d8e8",lineHeight:1.7,fontWeight:500}}>
            Your life is the dungeon.<br/>
            <span style={{color:"#38bdf8",fontWeight:700}}>You are the Hunter.</span>
          </p>
          <p style={{fontSize:13,color:"#64748b",lineHeight:1.7,marginTop:10}}>
            Most productivity apps feel like chores. ARISE turns your daily goals — work, gym, learning, habits — into a leveling system. Complete targets. Earn XP. Rise through the ranks.
          </p>
        </div>

        {/* Feature Cards */}
        {[
          { icon:"⚡", delay:"0.2s", title:"Set Your Targets", desc:"Create tasks for any area of your life — Work, Health, Learning, Creative. Set goals, pick a tracking metric, and the system does the math." },
          { icon:"📋", delay:"0.3s", title:"Log Reports & Earn XP", desc:"Every time you complete a session, log a report. Your XP grows. Your level rises. Stay consistent and watch your hunter rank climb." },
          { icon:"🏆", delay:"0.4s", title:"Compete Globally", desc:"Opt into the global leaderboard and see how you rank against hunters worldwide. Your top category earns you a title." },
          { icon:"🎮", delay:"0.5s", title:"Built for the Grind", desc:"Dark holographic UI. Animated level-ups. Awakening screen every session. This isn't a spreadsheet — it's a system." },
        ].map(({icon,delay,title,desc})=>(
          <div key={title} className="intro-card" style={{animationDelay:delay,opacity:0}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:14}}>
              <div style={{fontSize:22,marginTop:2,flexShrink:0}}>{icon}</div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:"#e2e8f0",marginBottom:5,
                  fontFamily:"'Share Tech Mono',monospace",letterSpacing:1}}>{title}</div>
                <div style={{fontSize:13,color:"#64748b",lineHeight:1.6}}>{desc}</div>
              </div>
            </div>
          </div>
        ))}

        {/* System status line */}
        <div style={{textAlign:"center",margin:"28px 0 20px",
          fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:"rgba(56,189,248,0.4)",letterSpacing:3,
          animation:"intro_glow 2s ease infinite"}}>
          [ SYSTEM READY — AWAITING HUNTER ]
        </div>

        {/* CTA */}
        <button className="intro-enter-btn" onClick={onEnter}>
          ▶ &nbsp; ENTER THE SYSTEM
        </button>

        <div style={{textAlign:"center",marginTop:16,fontSize:11,color:"#334155",
          fontFamily:"'Share Tech Mono',monospace",letterSpacing:1}}>
          FREE TO PLAY · NO ADS · LEVEL UP FOR REAL
        </div>

      </div>
    </div>
  );
}

// ─── Splash Screen ───────────────────────────────────────────────────────────
const ARISE_LOGO_B64 = "/icons/playstore_512.png";
function SplashScreen(){
  return(
    <div style={{position:"fixed",inset:0,background:"#020810",
      display:"flex",alignItems:"center",justifyContent:"center"}}>
      <style>{`
        @keyframes spl_outerRing{
          0%{clip-path:polygon(50% 50%,50% 0%,50% 0%,50% 0%,50% 0%,50% 0%,50% 0%,50% 0%,50% 0%,50% 0%)}
          100%{clip-path:polygon(50% 50%,50% 0%,100% 0%,100% 100%,0% 100%,0% 0%,50% 0%,50% 0%,50% 0%,50% 0%)}
        }
        @keyframes spl_reveal_outer{
          0%{opacity:0}
          5%{opacity:1}
          40%{opacity:1}
          60%{opacity:0}
          100%{opacity:0}
        }
        @keyframes spl_reveal_inner{
          0%,35%{opacity:0}
          40%{opacity:1}
          70%{opacity:1}
          85%{opacity:0}
          100%{opacity:0}
        }
        @keyframes spl_reveal_logo{
          0%,65%{opacity:0}
          75%{opacity:1}
          100%{opacity:1}
        }
        @keyframes spl_arise{
          0%,78%{opacity:0;transform:translateY(10px)}
          90%,100%{opacity:1;transform:translateY(0)}
        }
        @keyframes spl_levelup{
          0%,88%{opacity:0}
          100%{opacity:1}
        }
        @keyframes spl_rotate{
          from{transform:rotate(0deg)}
          to{transform:rotate(360deg)}
        }
        @keyframes spl_rotate_rev{
          from{transform:rotate(0deg)}
          to{transform:rotate(-360deg)}
        }
      `}</style>

      <div style={{position:"relative",width:240,height:270}}>

        {/* Layer 1: Outer ring only — SVG drawn ring animating */}
        <svg width="240" height="240" viewBox="0 0 240 240"
          style={{position:"absolute",top:0,left:0,zIndex:1}}>
          <circle cx="120" cy="120" r="115"
            fill="none" stroke="#1a2535" strokeWidth="12"/>
          <circle cx="120" cy="120" r="115"
            fill="none" stroke="#2a3f5a" strokeWidth="8"
            strokeDasharray="722" strokeDashoffset="722"
            strokeLinecap="round" transform="rotate(-90 120 120)"
            style={{strokeDashoffset:0}}>
            <animate attributeName="stroke-dashoffset"
              from="722" to="0" dur="0.7s" fill="freeze"/>
          </circle>
        </svg>

        {/* Layer 2: Inner ring + blue glow — SVG */}
        <svg width="240" height="240" viewBox="0 0 240 240"
          style={{position:"absolute",top:0,left:0,zIndex:2,opacity:0,
            animation:"spl_reveal_inner 3s ease forwards"}} >
          {/* Inner dark ring */}
          <circle cx="120" cy="120" r="100"
            fill="#060e1a" stroke="#1a2535" strokeWidth="6"/>
          {/* Blue glow arc */}
          <circle cx="120" cy="120" r="107"
            fill="none" stroke="#38bdf8" strokeWidth="16"
            strokeDasharray="185 690" strokeLinecap="round"
            transform="rotate(-200 120 120)" opacity="0.9"/>
          {/* Inner ring line */}
          <circle cx="120" cy="120" r="102"
            fill="none" stroke="#38bdf8" strokeWidth="1.5" opacity="0.5"/>
        </svg>

        {/* Layer 3: Full logo image revealed */}
        <div style={{
          position:"absolute",top:0,left:0,width:240,height:240,
          zIndex:3,
          opacity:0,
          animation:"spl_reveal_logo 3s ease forwards",
        }}>
          <img src={ARISE_LOGO_B64} alt="ARISE"
            style={{width:240,height:240,objectFit:"cover",borderRadius:"50%"}}/>
        </div>

        {/* Layer 4: ARISE text */}
        <div style={{
          position:"absolute",bottom:28,left:0,right:0,
          textAlign:"center",zIndex:4,
          fontFamily:"'Share Tech Mono','Courier New',monospace",
          fontSize:26,fontWeight:900,color:"#38bdf8",
          letterSpacing:5,
          opacity:0,
          animation:"spl_arise 3s ease forwards",
        }}>ARISE</div>

        {/* Layer 5: LEVEL UP text */}
        <div style={{
          position:"absolute",bottom:8,left:0,right:0,
          textAlign:"center",zIndex:4,
          fontFamily:"'Share Tech Mono','Courier New',monospace",
          fontSize:9,fontWeight:700,color:"#38bdf8",
          letterSpacing:5,
          opacity:0,
          animation:"spl_levelup 3s ease forwards",
        }}>LEVEL UP</div>

      </div>
    </div>
  );
}
export default function App(){
  const [user,setUser]             = useState(null);
  const [playerName,setPlayerName] = useState("Player");
  const [isAdmin,setIsAdmin]       = useState(false);
  const [showAdmin,setShowAdmin]   = useState(false);
  const [sysMessages,setSysMessages] = useState([]);
  const [showMsgInbox,setShowMsgInbox] = useState(false);
  const showMsgInboxRef = useRef(false);
  const dismissedMsgIds = useRef(new Set());
  const sessionStart = useRef(Date.now()); // only show popups for messages received after app loaded
  const setShowMsgInboxAndRef = (val) => {
    showMsgInboxRef.current = val;
    setShowMsgInbox(val);
  };
  const [pendingMsg,setPendingMsg]   = useState(null);
  const [isPremium,setIsPremium]     = useState(false);
  const [tipsSeen,setTipsSeen]       = useState([]);
  const [tipsLastDate,setTipsLastDate] = useState(null);
  const [tipsShownToday,setTipsShownToday] = useState(0);
  const [activeTip,setActiveTip]     = useState(null);
  const activeSecondsRef = useRef(0);
  // eslint-disable-next-line no-unused-vars
  const [replyingTo,setReplyingTo]   = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [replyText,setReplyText]     = useState("");
  // eslint-disable-next-line no-unused-vars
  const [replySent,setReplySent]     = useState({});
  const [userReplies,setUserReplies] = useState([]);
  const [authChecked,setAuthChecked] = useState(false);
  const [showIntro,setShowIntro] = useState(false); // shown every open until logged in
  const [isOffline,setIsOffline] = useState(!navigator.onLine);
  const [pendingSync,setPendingSync] = useState(false);
  const [activeTab,setActiveTab] = useState("home"); // "home" | "ranks" | "settings"
  const [settingsPage,setSettingsPage] = useState(null); // null|"username"|"xp"|"premium-info"|"how-arise"|"how-ranking"|"privacy"|"contact"
  const [globalOptedIn,setGlobalOptedIn] = useState(false);
  const [globalLevelUpScreen,setGlobalLevelUpScreen] = useState(null); // {from,to}
  const [dualLevelUp,setDualLevelUp] = useState(null); // {personal,global}
  const [awakening,setAwakening] = useState(false);
  const [awakeFromLogin,setAwakeFromLogin] = useState(false);
  const [showOnboarding,setShowOnboarding] = useState(false);
  const [onboardingSkipped,setOnboardingSkipped] = useState(false);
  const [dashEnter,setDashEnter]   = useState(false);
  const [showLevelModal,setShowLevelModal] = useState(false);
  const [tasks,setTasks]           = useState([]);
  const [ready,setReady]           = useState(false);
  const [openId,setOpenId]         = useState(null);
  const [filterCat,setFilterCat]   = useState("all");
  const [cardPeriod,setCardPeriod] = useState("weekly");
  const [showAdd,setShowAdd]       = useState(false);
  const [notif,setNotif]           = useState(null);
  const [levelUpScreen,setLevelUpScreen] = useState(null);
  const [confirmDel,setConfirmDel] = useState(false);
  const [confirmSignOut,setConfirmSignOut] = useState(false);
  const [confirmQuickLog,setConfirmQuickLog] = useState(null); // task object pending confirmation
  const [showEditModal,setShowEditModal] = useState(false);

  // Report form
  const [lVal,setLVal]       = useState({});
  const [lStatus,setLStatus] = useState("In Progress");
  const [lProg,setLProg]     = useState("0");
  const [lNote,setLNote]     = useState("");
  const [lDate,setLDate]     = useState("");

  // Edit report
  const [editId,setEditId]   = useState(null);
  const [eVal,setEVal]       = useState({});
  const [eStatus,setEStatus] = useState("");
  const [eProg,setEProg]     = useState("");
  const [eNote,setENote]     = useState("");

  // Sheet panels
  const [sheetPeriod,setSheetPeriod]       = useState("weekly");
  const [showMetricEdit,setShowMetricEdit] = useState(false);
  const [editingMetric,setEditingMetric]   = useState("time");
  const [pendingUnit,setPendingUnit]       = useState("");
  const [metricModal,setMetricModal]       = useState(false);

  // New target form
  const [nTitle,setNTitle]         = useState("");
  const [nDesc,setNDesc]           = useState("");
  const [nCat,setNCat]             = useState("work");
  const [nMetric,setNMetric]       = useState("time");
  const [nCustomUnit,setNCustomUnit] = useState("");
  const [nGoals,setNGoals]         = useState({});

 // ── Network status ────────────────────────────────────────────────────────
  useEffect(()=>{
    const goOnline = async () => {
      setIsOffline(false);
      if(user && getPendingLogs(user.id).length > 0){
        setPendingSync(true);
        const pending = getPendingLogs(user.id);
        setTasks(prev=>{
          let updated = [...prev];
          pending.forEach(({taskId, entry})=>{
            updated = updated.map(t=>t.id===taskId
              ? {...t, total:t.total+1, logs:[entry,...t.logs]}
              : t);
          });
          const affectedIds = new Set(pending.map(p=>p.taskId));
          updated.filter(t=>affectedIds.has(t.id)).forEach(t=>saveTask(user.id,t));
          cacheTasks(user.id, updated);
          return updated;
        });
        clearPendingLogs(user.id);
        setPendingSync(false);
      }
    };
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return()=>{ window.removeEventListener("online",goOnline); window.removeEventListener("offline",goOffline); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[user]);

  // ── Auth state machine ────────────────────────────────────────────────────
  // States: "loading" → "unauthenticated" → "naming" → "ready"
  // "loading"         = splash screen, checking session
  // "unauthenticated" = no session, show AuthScreen
  // "naming"          = session exists but no valid name, AuthScreen handles naming
  // "ready"           = fully initialised, show app
  const authStateRef = useRef("loading");
  function setAuthState(s){ authStateRef.current = s; }

  useEffect(()=>{
    // Only SIGNED_OUT needs handling here — all other transitions go through
    // initUserData (called by handleAuth from AuthScreen, or getSession below)
    const {data:{subscription}} = supabase.auth.onAuthStateChange((event)=>{
      if(event==="SIGNED_OUT"){
        setAuthState("unauthenticated");
        setUser(null); setTasks([]); setReady(false);
        setAwakening(false); setAuthChecked(true); setShowIntro(true);
      }
    });

    // Single source of truth for session restore on mount
    supabase.auth.getSession().then(async({data:{session}})=>{
      if(!session?.user){
        setAuthState("unauthenticated");
        setAuthChecked(true); setShowIntro(true);
        return;
      }
      try{
        const profile = await loadProfile(session.user.id);
        const name = profile?.player_name;
        if(!name || !isValidName(name)){
          // Has session but no name — AuthScreen will handle naming
          setAuthState("naming");
          setAuthChecked(true);
          return;
        }
        setAuthState("ready");
        await initUserData(session.user, name, false, false);
      }catch(e){
        console.error("Session restore error:", e);
        setAuthState("unauthenticated");
      }
      setAuthChecked(true);
    });

    return()=>subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  async function initUserData(u, name, isNewUser=false, isExplicitLogin=false){
    setUser(u);
    setPlayerName(sanitizeName(name)||name);

    // Load cached profile data immediately for offline use
    const cachedProfile = getCachedProfile(u.id);

    if(!isOnline()){
      // Offline — use cached tasks and profile, skip all network calls
      const cached = getCachedTasks(u.id);
      if(cached && cached.length > 0){
        setTasks(cached);
        setReady(true);
        if(cachedProfile){
          setIsAdmin(!!cachedProfile.isAdmin);
          setIsPremium(!!cachedProfile.isPremium);
          setGlobalOptedIn(!!cachedProfile.globalOptedIn);
        }
        setAwakeFromLogin(isExplicitLogin);
        setAwakening(true);
      } else {
        // No cache — can't do much offline
        setReady(true);
        setShowOnboarding(true);
      }
      return;
    }

    // Online — normal flow
    if(u.email && isValidName(sanitizeName(name))) saveProfile(u.id, sanitizeName(name)||name, u.email);
    const {data:adminRow}=await supabase.from("admins").select("email").eq("email",u.email).single();
    const isAdmin = !!adminRow;
    setIsAdmin(isAdmin);
    const cleanName=sanitizeName(name)||name;
    const {data:premByOwner}=await supabase.from("premium_usernames").select("name").eq("granted_to",u.id).maybeSingle();
    const {data:premByName}=await supabase.from("premium_usernames").select("name,granted_to").eq("name",cleanName).maybeSingle();
    const ownsByName=premByName&&(premByName.granted_to===u.id||premByName.granted_to===null);
    const isPrem = !!(premByOwner||ownsByName);
    setIsPremium(isPrem);
    const {data:profileRow}=await supabase.from("profiles")
      .select("global_ranking_opted_in,tips_seen,tips_last_date,tips_shown_today").eq("id",u.id).single();
    const globalOpted = !!(profileRow?.global_ranking_opted_in);
    setGlobalOptedIn(globalOpted);
    setTipsSeen(profileRow?.tips_seen||[]);
    setTipsLastDate(profileRow?.tips_last_date||null);
    setTipsShownToday(profileRow?.tips_shown_today||0);

    // Cache profile metadata for offline use
    cacheProfile(u.id, {isAdmin, isPremium:isPrem, globalOptedIn:globalOpted});

    const data=await loadTasks(u.id); // eslint-disable-line no-unused-vars
    const isFirstTime=!data||data.length===0;
    let finalTasks = [];
    if(!isFirstTime){
      finalTasks = data.map(t=>({...t,id:String(t.id),metric:t.metric||"time",goals:t.goals||{},createdAt:t.createdAt||new Date(Date.now()-30*86400000).toISOString()}));
      setTasks(finalTasks);
      cacheTasks(u.id, finalTasks); // cache for offline use
    } else {
      setTasks([]);
    }
    setReady(true);
    fetchMessages(u.id);
    if(window._msgPoll) clearInterval(window._msgPoll);
    window._msgPoll=setInterval(()=>fetchMessages(u.id),10000);
    if(isNewUser){ setTasks([]); setShowOnboarding(true); }
    else if(isFirstTime){ setShowOnboarding(true); }
    else{ setAwakeFromLogin(isExplicitLogin); setAwakening(true); }
  }

  // Called by AuthScreen when login/signup/naming completes successfully
  function handleAuth(u, name, isNew=false){
    setAuthState("ready");
    setAuthChecked(true);
    setUser(u);
    initUserData(u, name, isNew, true);
  }

  function handleOnboardingComplete(tasksOrNull, skipped){
    setShowOnboarding(false);
    if(skipped) setOnboardingSkipped(true);
    const finalTasks=tasksOrNull&&tasksOrNull.length>0?tasksOrNull:(!tasksOrNull&&!skipped?SEED:[]);
    if(finalTasks.length>0){
      setTasks(finalTasks);
      if(user) finalTasks.forEach(t=>saveTask(user.id,t));
    }
    setAwakeFromLogin(true);
    setAwakening(true);
    if(!skipped&&finalTasks.length>0){
      setTimeout(()=>{
        setAwakening(false);
        setTimeout(()=>setShowLevelModal(true),400);
      },3500);
    }
  }

  async function fetchMessages(userId){
    const {data}=await supabase.from("system_messages")
      .select("*")
      .or(`recipient_id.eq.${userId},recipient_id.is.null`)
      .order("created_at",{ascending:false});
    if(data){
      setSysMessages(data);
      const unseen=data.filter(m=>
        m.status==="unread" &&
        !dismissedMsgIds.current.has(m.id) &&
        new Date(m.created_at).getTime() > sessionStart.current
      );
      const unseenDirect=unseen.filter(m=>m.recipient_id===userId);
      const unseenGlobal=unseen.filter(m=>m.recipient_id===null);
      // If inbox is open, auto-mark all unread direct messages as read
      if(showMsgInboxRef.current && unseenDirect.length>0){
        unseenDirect.forEach(m=>{
          supabase.from("system_messages").update({status:"read"}).eq("id",m.id);
        });
        setSysMessages(prev=>prev.map(m=>
          unseenDirect.find(u=>u.id===m.id)?{...m,status:"read"}:m
        ));
        setPendingMsg(null);
      } else if(unseenDirect.length>0){
        setPendingMsg(unseenDirect[0]);
      } else if(unseenGlobal.length>0){
        setPendingMsg(unseenGlobal[0]);
      } else {
        setPendingMsg(null);
      }
    }
    const {data:replies}=await supabase.from("user_replies")
      .select("*").eq("user_id",userId).order("created_at",{ascending:true});
    if(replies) setUserReplies(replies);
  }

  // eslint-disable-next-line no-unused-vars
  const QUICK_REPLIES = [
    {label:"💸 Payment Sent", text:"I have sent the payment. Please check and confirm."},
    {label:"🎁 Gift Card", text:"I have a gift card to share. Please let me know how to send it to you."},
    {label:"✅ Done", text:"Done! Please let me know if you need anything else."},
    {label:"❓ Need Help", text:"I need some help with this. Can you guide me?"},
  ];

  // eslint-disable-next-line no-unused-vars
  async function sendUserReply(msgId, text){
    if(!text.trim()) return;
    await supabase.from("user_replies").insert({
      message_id: msgId,
      user_id: user.id,
      player_name: playerName,
      email: user.email||null,
      reply: text.trim(),
    });
    setReplySent(prev=>({...prev,[msgId]:true}));
    setReplyingTo(null);
    setReplyText("");
    markMessageRead(msgId);
    // Refresh replies to update thread view
    const {data}=await supabase.from("user_replies").select("*").eq("user_id",user.id).order("created_at",{ascending:true});
    if(data) setUserReplies(data);
  }

  async function markMessageRead(msgId){
    await supabase.from("system_messages").update({status:"read"}).eq("id",msgId);
    setSysMessages(prev=>prev.map(m=>m.id===msgId?{...m,status:"read"}:m));
    setPendingMsg(null);
  }
  // eslint-disable-next-line no-unused-vars
  async function markAllRead(){
    const ids=sysMessages.filter(m=>m.status==="unread").map(m=>m.id);
    if(!ids.length) return;
    await supabase.from("system_messages").update({status:"read"}).in("id",ids);
    setSysMessages(prev=>prev.map(m=>({...m,status:"read"})));
  }
  // eslint-disable-next-line no-unused-vars
  async function markMessageUnread(msgId){
    await supabase.from("system_messages").update({status:"unread"}).eq("id",msgId);
    setSysMessages(prev=>prev.map(m=>m.id===msgId?{...m,status:"unread"}:m));
  }

  async function handleSignOut(){
    if(window._msgPoll) clearInterval(window._msgPoll);
    await supabase.auth.signOut();
    setUser(null); setTasks([]); setReady(false); setAwakening(false);
  }

  // ─── Discovery tips: pick the next unseen tip and persist it ──────────────
  function showNextTip(){
    if(!user) return;
    const today = new Date().toISOString().slice(0,10);
    const shownToday = tipsLastDate===today ? tipsShownToday : 0;
    if(shownToday>=2) return;

    let seen = tipsSeen;
    let pool = TIPS.filter(t=>!seen.includes(t.id) && (!t.condition || t.condition({isPremium})));
    if(pool.length===0){
      // Everyone's been shown once — start the cycle over
      seen = [];
      pool = TIPS.filter(t=>!t.condition || t.condition({isPremium}));
    }
    if(pool.length===0) return; // no eligible tips at all right now

    const tip = pool[0];
    const newSeen = [...seen, tip.id];
    const newShownToday = shownToday+1;

    setTipsSeen(newSeen);
    setTipsShownToday(newShownToday);
    setTipsLastDate(today);
    setActiveTip(tip);

    supabase.from("profiles").update({
      tips_seen:newSeen, tips_last_date:today, tips_shown_today:newShownToday
    }).eq("id",user.id).then(()=>{});
  }

  // ─── Auto-enable push notifications ────────────────────────────────────────
  // Triggers the browser/OS permission prompt automatically each time the
  // user opens the app — including resuming from background, since a TWA
  // often stays alive rather than fully reloading — as long as they haven't
  // made a real decision yet. Two true stopping points, both enforced by the
  // browser itself, not by us: "granted" (already on) and "denied" (they
  // explicitly tapped Don't Allow — browsers refuse to re-prompt after that,
  // no matter how often we ask, so this can never turn into nagging). Any
  // other outcome ("default" — dismissed without choosing, or never asked)
  // is fair game to try again next time they open the app.
  const pushCheckInFlight = useRef(false);
  useEffect(()=>{
    const onMainApp = ready && user && !showOnboarding && !awakening;
    if(!onMainApp) return;

    async function attempt(){
      if(pushCheckInFlight.current) return;
      pushCheckInFlight.current = true;
      try{
        const status = await getPushStatus();
        if(status==="off"){ // never asked (or asked-and-dismissed), or granted-but-not-subscribed
          await enablePush(supabase, user.id);
        }
        // "denied" and "unsupported" are left alone entirely, on purpose.
      } finally {
        pushCheckInFlight.current = false;
      }
    }

    attempt(); // on mount / whenever we land on the main app

    function onVisible(){
      if(document.visibilityState==="visible") attempt(); // app re-opened from background
    }
    document.addEventListener("visibilitychange", onVisible);
    return ()=>document.removeEventListener("visibilitychange", onVisible);
  },[ready, user, showOnboarding, awakening]);

  // ─── Discovery tips: active-time-in-app session timer ─────────────────────
  // Only counts time while the tab/app is actually visible in the foreground.
  // Fires once at 1 min (first tip) and once at 2 min (second tip), max 2/day.
  useEffect(()=>{
    const onMainApp = ready && user && !showOnboarding && !awakening;
    if(!onMainApp) return;

    const interval = setInterval(()=>{
      if(document.visibilityState!=="visible") return;
      activeSecondsRef.current += 1;
      if(activeSecondsRef.current===60 || activeSecondsRef.current===120){
        showNextTip();
      }
    },1000);

    return ()=>clearInterval(interval);
  // Re-creating the interval when tip state changes (only happens ~2x/day,
  // right after a tip fires) keeps showNextTip() reading fresh values instead
  // of a stale closure from when the effect first ran. activeSecondsRef itself
  // is unaffected by this, since it lives outside React state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[ready, user, showOnboarding, awakening, tipsSeen, tipsShownToday, tipsLastDate, isPremium]);

  useEffect(()=>{
    if(!activeTip) return;
    const t = setTimeout(()=>setActiveTip(null), 8000);
    return ()=>clearTimeout(t);
  },[activeTip]);

  // Refresh messages whenever inbox is opened, and clear any pending popup
  useEffect(()=>{
    if(showMsgInbox && user){
      // Mark all current unread as dismissed so they never re-popup
      sysMessages.filter(m=>m.status==="unread").forEach(m=>dismissedMsgIds.current.add(m.id));
      setPendingMsg(null);
      fetchMessages(user.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[showMsgInbox]);



  const task=tasks.find(t=>t.id===openId)??null;

  // Auth gate
  if(!authChecked) return <SplashScreen/>;
  if(!user && showIntro) return <IntroScreen onEnter={()=>setShowIntro(false)}/>;
  if(!user) return <AuthScreen onAuth={handleAuth}/>;
  if(showOnboarding) return <OnboardingFlow playerName={playerName} onComplete={handleOnboardingComplete} onSkip={()=>handleOnboardingComplete(null,true)}/>
  if(awakening) return <AwakeningScreen playerName={playerName} fromLogin={awakeFromLogin} isPremium={isPremium} level={xpToLevel(calcTotalXP(tasks),calcL1Threshold(tasks)).level} onDone={()=>{AudioEngine.enterSystem();setAwakening(false);setDashEnter(true);setTimeout(()=>setDashEnter(false),1400);}}/>;
  if(!ready) return <div style={{minHeight:"100vh",background:"#000"}}/>;

  function showNotif(msg,sub,type="info",anim){
    setNotif({msg,sub,type,anim});
    setTimeout(()=>setNotif(null),3000);
  }

  function openTask(t){
    setOpenId(t.id);
    setLVal({}); setLNote(""); setLDate(""); setLStatus("In Progress"); setLProg("30"); setLDate("");
    setConfirmDel(false); setEditId(null); setShowEditModal(false);
    setShowMetricEdit(false); setSheetPeriod("weekly");
  }
  function closeSheet(){setOpenId(null);setEditId(null);setShowEditModal(false);setShowMetricEdit(false);setMetricModal(false);}

  function buildVal(metric,val){
    if(metric==="time")return{totalMins:(parseInt(val.h||0)*60)+parseInt(val.m||0)};
    return{amount:parseFloat(val.amount||0)};
  }
  function logDelta(metric,v){return metric==="time"?(v?.totalMins||0):parseFloat(v?.amount||0);}

  // Check if adding a log caused a level up — XP is log-based now
 function checkLevelUp(tasksBefore, tasksAfter){
  const base=calcL1Threshold(tasksAfter);
  const xpBefore=calcTotalXP(tasksBefore);
  const xpAfter=calcTotalXP(tasksAfter);
  const lvBefore=xpToLevel(xpBefore,base).level;
  const lvAfter=xpToLevel(xpAfter,base).level;
  const personalUp = lvAfter>lvBefore ? {from:lvBefore,to:lvAfter} : null;

  // Global level check
  let globalUp = null;
  if(globalOptedIn){
    const gBefore=calcGlobalLevel(tasksBefore);
    const gAfter=calcGlobalLevel(tasksAfter);
    if(gAfter.level>gBefore.level) globalUp={from:gBefore.level,to:gAfter.level};
  }

  if(personalUp||globalUp){
    setTimeout(()=>{
      if(personalUp&&globalUp) setDualLevelUp({personal:personalUp,global:globalUp});
      else if(personalUp) setLevelUpScreen(personalUp);
      else setGlobalLevelUpScreen(globalUp);
    },200);
  }
}

  function doQuickLog(t){
    const lastLog = t.logs?.[0];
    const logStatus = lastLog?.status === "Done" ? "Done" : "In Progress";
    const logProgress = logStatus === "Done" ? 100 : 30;
    const unit = t.metric==="time"?{totalMins:1}:{amount:1};
    const entry={id:uid(),date:new Date().toISOString(),metric:t.metric,value:unit,status:logStatus,progress:logProgress,note:""};
    setTasks(prev=>{
      const updated=prev.map(x=>x.id===t.id?{...x,status:logStatus,progress:logProgress,total:x.total+1,logs:[entry,...x.logs]}:x);
      checkLevelUp(prev,updated);
      if(user){
        if(isOnline()){
          updated.filter(x=>x.id===t.id).forEach(x=>saveTask(user.id,x));
          if(globalOptedIn) saveGlobalStats(user.id,updated);
        } else {
          queueOfflineLog(user.id,t.id,entry);
        }
        cacheTasks(user.id,updated);
      }
      return updated;
    });
    showNotif("Report submitted.","Progress recorded, "+playerName+".","info");
  }

  function quickLog(e,t){
    e.stopPropagation();
    AudioEngine.buzz();
    const today = new Date().toDateString();
    const hasToday = (t.logs||[]).some(l=>new Date(l.date).toDateString()===today);
    if(hasToday){
      setConfirmQuickLog(t);
    } else {
      doQuickLog(t);
    }
  }

  function submitLog(){
    const tid=openId; if(!tid)return;
    AudioEngine.buzz();
    const mv=buildVal(task.metric,lVal);
    const delta=logDelta(task.metric,mv);
    const prog=parseInt(lProg);
    const logDate=lDate?new Date(lDate+"T12:00:00").toISOString():new Date().toISOString();
    const entry={id:uid(),date:logDate,metric:task.metric,value:mv,status:lStatus,progress:prog,note:lNote.trim()};
    setTasks(prev=>{
      const updated=prev.map(t=>t.id===tid?{...t,status:lStatus,progress:prog,total:t.total+delta,logs:[entry,...t.logs]}:t);
      checkLevelUp(prev,updated);
      if(user){
        if(isOnline()){
          updated.filter(t=>t.id===tid).forEach(t=>saveTask(user.id,t));
          if(globalOptedIn) saveGlobalStats(user.id,updated);
        } else {
          queueOfflineLog(user.id, tid, entry);
        }
        cacheTasks(user.id, updated);
      }
      return updated;
    });
    setLVal({}); setLNote(""); setLDate("");
    const wp=getPeriodProgress({...task,logs:[entry,...task.logs]},"weekly");
    if(!wp.met&&POSITIVE.includes(lStatus))showNotif("Report filed.","Keep pushing, "+playerName+".","info");
  }

  function startEdit(log){
    setEditId(log.id);
    const lm=log.metric||task.metric;
    if(lm==="time"){const tm=log.value?.totalMins||0;setEVal({h:String(Math.floor(tm/60)),m:String(tm%60)});}
    else setEVal({amount:String(log.value?.amount||0)});
    setEStatus(log.status); setEProg(String(log.progress)); setENote(log.note||"");
  }
  function saveEdit(){
    const tid=openId; if(!tid||!editId)return;
    const newVal=buildVal(task.metric,eVal);
    const newDelta=logDelta(task.metric,newVal);
    setTasks(prev=>{
      const updated=prev.map(t=>{
        if(t.id!==tid)return t;
        const old=t.logs.find(l=>l.id===editId);
        const oldDelta=logDelta(t.metric,old?.value||{});
        const logs=t.logs.map(l=>l.id===editId?{...l,value:newVal,metric:t.metric,status:eStatus,progress:parseInt(eProg),note:eNote.trim()}:l);
        const latest=logs[0];
        return{...t,total:Math.max(0,t.total+(newDelta-oldDelta)),logs,status:latest?.status||t.status,progress:latest?.progress??t.progress};
      });
      if(user){
        const changed=updated.find(t=>t.id===tid);
        if(isOnline()) saveTask(user.id,changed);
        cacheTasks(user.id,updated);
      }
      return updated;
    });
    setEditId(null);
    showNotif("Report amended.","","info");
  }
  function deleteLog(logId){
    const tid=openId; if(!tid)return;
    setTasks(prev=>{
      const updated=prev.map(t=>{
        if(t.id!==tid)return t;
        const log=t.logs.find(l=>l.id===logId);
        const delta=logDelta(t.metric,log?.value||{});
        const logs=t.logs.filter(l=>l.id!==logId);
        const latest=logs[0];
        return{...t,total:Math.max(0,t.total-delta),logs,status:latest?.status||t.status,progress:latest?.progress??t.progress};
      });
      if(user){
        const changed=updated.find(t=>t.id===tid);
        if(isOnline()) saveTask(user.id,changed);
        cacheTasks(user.id,updated);
      }
      return updated;
    });
  }

  function saveTaskEdit(newTitle, newCat, newMetric, newCustomUnit, newGoals){
    AudioEngine.chime();
    setTasks(prev=>{
      const updated=prev.map(t=>{
        if(t.id!==openId)return t;
        const newTotal=sumLogs(t.logs.filter(l=>l.metric===newMetric),newMetric);
        return{...t,title:newTitle,cat:newCat,metric:newMetric,customUnit:newCustomUnit||"",goals:newGoals,total:newTotal};
      });
      if(user) updated.filter(t=>t.id===openId).forEach(t=>saveTask(user.id,t));
      return updated;
    });
    setShowEditModal(false);
    // Redirect to XP setup after editing target
    setTimeout(()=>{AudioEngine.open();setShowLevelModal(true);},350);
    showNotif("Target updated. Review XP below.",playerName+".","success");
  }


  // eslint-disable-next-line no-unused-vars
  function saveGoals(goals){
    setTasks(prev=>prev.map(t=>t.id===openId?{...t,goals}:t));
    showNotif("Target parameters updated.","System acknowledged, "+playerName+".","success");
  }

  function requestMetricChange(){
    if(!task)return;
    if(task.logs.length===0){applyMetricChange(false);return;}
    setMetricModal(true);
  }
  function applyMetricChange(reset){
    setTasks(prev=>{
      const updated=prev.map(t=>t.id!==openId?t:{...t,metric:editingMetric,customUnit:pendingUnit||"",total:reset?0:t.total,logs:reset?[]:t.logs,goals:{}});
      if(user) updated.filter(t=>t.id===openId).forEach(t=>saveTask(user.id,t));
      return updated;
    });
    setShowMetricEdit(false); setMetricModal(false);
  }

  function addTask(){
    if(!nTitle.trim())return;
    AudioEngine.chime();
    const t={id:uid(),createdAt:new Date().toISOString(),title:nTitle.trim(),desc:nDesc.trim(),cat:nCat,status:"Not Started",
      progress:0,logs:[],total:0,metric:nMetric,customUnit:nCustomUnit,goals:nGoals};
    setTasks(prev=>{
      if(user) saveTask(user.id,t);
      return [t,...prev];
    });
    setNTitle("");setNDesc("");setNCat("work");setNMetric("time");setNCustomUnit("");setNGoals({});setShowAdd(false);
    // Redirect to XP setup after creating target
    setTimeout(()=>{AudioEngine.open();setShowLevelModal(true);},350);
    showNotif("New target registered. Set XP below.",playerName+".","info","line");
  }
  function saveXP(xpMap){
    setTasks(prev=>{
      const updated=prev.map(t=>({...t,xp:parseInt(xpMap[t.id])||DEFAULT_XP}));
      if(user) updated.forEach(t=>saveTask(user.id,t));
      return updated;
    });
    setShowLevelModal(false);
    showNotif("XP values updated.","Level recalculated, "+playerName+".","success");
  }

  function deleteTask(){
    AudioEngine.chime();
    if(user) deleteTaskDB(user.id, openId);
    setTasks(prev=>prev.filter(t=>t.id!==openId));
    closeSheet();
    showNotif("Target removed from system.","","warn");
  }

  const filtered=filterCat==="all"?tasks:tasks.filter(t=>t.cat===filterCat);
  const dashTasks=tasks.filter(t=>getEffectiveGoalAmt(t,cardPeriod)>0);
  const dashDone=dashTasks.filter(t=>getPeriodProgress(t,cardPeriod).met).length;
  const totalDays=realDaysInPeriod(cardPeriod);
  const daysLeft=daysLeftInPeriod(cardPeriod);

  // ─── Render ───────────────────────────────────────────────────────────────
  return(
    <div style={{minHeight:"100vh",background:"#02060e",color:"#c8d8e8",fontFamily:"'Outfit','DM Sans',sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Share+Tech+Mono&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;-webkit-user-select:none;user-select:none}
        input,textarea{-webkit-user-select:text;user-select:text}
        ::placeholder{color:#223}
        ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:#0a1a2a;border-radius:2px}
        input,button,textarea{font-family:inherit}
        .sys-panel{background:rgba(4,12,24,0.98);border:1px solid ${SYS_BORDER};border-radius:12px;padding:20px}
        .sys-label{font-family:'Share Tech Mono',monospace;font-size:10px;letter-spacing:3px;color:${SYS};opacity:0.7}
        .sys-tag{font-family:'Share Tech Mono',monospace;font-size:9px;letter-spacing:2px;color:${SYS};background:${SYS_DIM};padding:2px 7px;border-radius:4px;border:1px solid ${SYS_BORDER}}
        .sys-row{background:rgba(255,255,255,0.02);border:1px solid rgba(255,255,255,0.05);border-radius:9px;padding:10px 12px}
        .sys-row-active{background:${SYS_DIM};border:1px solid ${SYS_BORDER};border-radius:9px;padding:10px 12px}
        .sys-close{background:none;border:none;color:#334;cursor:pointer;font-size:16px;padding:2px 6px}
        .sys-btn{border-radius:8px;cursor:pointer;font-weight:600;font-family:'Share Tech Mono',monospace;letter-spacing:1px}
        .sys-btn-sm{padding:4px 10px;font-size:10px;letter-spacing:1px;font-family:'Share Tech Mono',monospace;border-radius:6px;background:${SYS_DIM};color:${SYS};border:1px solid ${SYS_BORDER};cursor:pointer;font-weight:700}
        .sys-btn-sm.warn{background:rgba(239,68,68,0.1);color:#ef4444;border-color:rgba(239,68,68,0.3)}
        .sys-btn-primary{background:${SYS_DIM};color:${SYS};border:1px solid ${SYS_BORDER};font-family:'Share Tech Mono',monospace;letter-spacing:2px;font-size:12px;font-weight:700;border-radius:9px;cursor:pointer}
        .sys-btn-primary:hover{background:rgba(56,189,248,0.18)}
        .sys-btn-block{padding:11px;background:rgba(255,255,255,0.03);color:#aac;border:1px solid rgba(255,255,255,0.07);font-size:13px;text-align:left;border-radius:9px;width:100%}
        .sys-btn-block.warn{background:rgba(239,68,68,0.06);color:#fca5a5;border-color:rgba(239,68,68,0.2)}
        .btn{border:none;border-radius:10px;cursor:pointer;font-weight:600;transition:all .15s}.btn:active{transform:scale(.95)}
        .overlay{position:fixed;inset:0;background:rgba(0,0,0,0.97);z-index:8500;display:flex;align-items:flex-end;justify-content:center;animation:fadeIn .18s;padding-bottom:90px}
        .sheet{background:#030912;border-top:1px solid ${SYS_BORDER};border-radius:18px 18px 0 0;width:100%;max-width:560px;max-height:calc(94vh - 90px);overflow-y:auto;padding-bottom:50px;animation:slideUp .3s cubic-bezier(.34,1.56,.64,1)}
        .field{background:rgba(56,189,248,0.04);border:1px solid rgba(56,189,248,0.15);border-radius:9px;color:#c8d8e8;padding:11px 13px;font-size:14px;outline:none;width:100%;transition:border-color .2s;font-family:'Outfit',sans-serif}
        .field:focus{border-color:${SYS};box-shadow:0 0 0 1px ${SYS_DIM}}
        .ptrack{height:4px;background:rgba(255,255,255,0.05);border-radius:2px;overflow:hidden}
        .pfill{height:100%;border-radius:2px;transition:width .5s ease}
        .quick{border:none;border-radius:8px;cursor:pointer;font-weight:800;font-size:18px;transition:all .15s;display:flex;align-items:center;justify-content:center;font-family:'Share Tech Mono',monospace}
        .quick:active{transform:scale(.85)}
        .ptab{border:none;border-radius:7px;cursor:pointer;font-size:10px;font-weight:700;padding:5px 10px;transition:all .15s;text-transform:uppercase;letter-spacing:2px;font-family:'Share Tech Mono',monospace}
        .card-target{transition:transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94),box-shadow 0.35s ease,opacity 0.35s ease;cursor:pointer;will-change:transform}
        .card-target:active{transform:scale(.97)}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes dashReveal{
          0%{opacity:0;transform:translateY(32px) scale(0.96);filter:brightness(2.5) blur(4px)}
          40%{opacity:1;filter:brightness(1.4) blur(1px)}
          70%{transform:translateY(-4px) scale(1.01);filter:brightness(1)}
          100%{opacity:1;transform:translateY(0) scale(1);filter:brightness(1)}
        }
        @keyframes flashIn{
          0%{opacity:1}
          100%{opacity:0}
        }
        @keyframes linesScan{
          0%{transform:translateY(-100%)}
          100%{transform:translateY(100%)}
        }
        @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
        @keyframes popIn{from{opacity:0;transform:translateY(-5px)}to{opacity:1;transform:translateY(0)}}
        @keyframes notifIn{from{opacity:0;transform:translateX(-50%) translateY(calc(-50% - 14px))}to{opacity:1;transform:translateX(-50%) translateY(-50%)}}
        @keyframes lineReveal{
          0%{clip-path:inset(0 50% 0 50%);}
          22%{clip-path:inset(0 calc(50% - 1.5px) 0 calc(50% - 1.5px));}
          100%{clip-path:inset(0 0 0 0);}
        }
        @keyframes lineGlow{
          0%{opacity:0;}
          15%{opacity:1;}
          45%{opacity:1;}
          100%{opacity:0;}
        }
        @keyframes tipSlideUp{
          0%{opacity:0;transform:translateY(16px);}
          100%{opacity:1;transform:translateY(0);}
        }
        @keyframes goldShimmer{
          0%{background-position:200% center}
          100%{background-position:-200% center}
        }
        @keyframes goldSweep{
          0%{left:-60%;opacity:0}
          10%{opacity:1}
          50%{left:120%;opacity:0.7}
          100%{left:120%;opacity:0}
        }
        @keyframes goldPulse{
          0%,100%{text-shadow:0 0 12px rgba(251,191,36,0.8),0 0 24px rgba(251,191,36,0.4)}
          50%{text-shadow:0 0 20px rgba(251,191,36,1),0 0 40px rgba(251,191,36,0.6),0 0 60px rgba(251,191,36,0.3)}
        }
        @keyframes blink{0%,100%{opacity:0.4}50%{opacity:1}}
        @keyframes completeFill{from{transform:scaleX(0)}to{transform:scaleX(1)}}
        @keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}
        @keyframes sparkFly{0%{opacity:1;transform:translate(0,0)}100%{opacity:0;transform:translate(var(--tx),var(--ty))}}
        .log-entry{animation:popIn .2s ease}
        input[type="date"]::-webkit-calendar-picker-indicator{filter:invert(0.3) sepia(1) saturate(3) hue-rotate(180deg);cursor:pointer}
        .card-target::-webkit-scrollbar{display:none}
        /* scanline overlay on main */
        .scanlines::after{content:'';position:fixed;inset:0;backgroundImage:repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,0,0,0.04) 3px,rgba(0,0,0,0.04) 4px);pointer-events:none;z-index:1}
      `}</style>

      {/* Dramatic entry flash */}
      {dashEnter&&(
        <div style={{position:"fixed",inset:0,zIndex:9998,pointerEvents:"none"}}>
          {/* White flash */}
          <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at center,rgba(56,189,248,0.35) 0%,rgba(0,0,0,0) 70%)`,animation:"flashIn 0.9s ease forwards"}}/>
          {/* Scan line sweep */}
          <div style={{position:"absolute",left:0,right:0,height:"30%",
            background:"linear-gradient(to bottom,transparent,rgba(56,189,248,0.06),transparent)",
            animation:"linesScan 0.8s ease forwards"}}/>
          {/* Corner brackets flash */}
          {[{top:0,left:0},{top:0,right:0},{bottom:0,left:0},{bottom:0,right:0}].map((pos,i)=>(
            <div key={i} style={{position:"absolute",...pos,width:40,height:40,
              borderTop:i<2?`2px solid ${SYS}`:"none",
              borderBottom:i>=2?`2px solid ${SYS}`:"none",
              borderLeft:i%2===0?`2px solid ${SYS}`:"none",
              borderRight:i%2===1?`2px solid ${SYS}`:"none",
              animation:"flashIn 1.2s ease forwards",opacity:0.8}}/>
          ))}
        </div>
      )}
      {levelUpScreen&&!dualLevelUp&&(
        <LevelUpScreen fromLevel={levelUpScreen.from} toLevel={levelUpScreen.to} onContinue={()=>setLevelUpScreen(null)}/>
      )}
      {globalLevelUpScreen&&!dualLevelUp&&(
        <DualLevelUpScreen personal={null} global={globalLevelUpScreen} onContinue={()=>setGlobalLevelUpScreen(null)}/>
      )}
      {dualLevelUp&&(
        <DualLevelUpScreen personal={dualLevelUp.personal} global={dualLevelUp.global} onContinue={()=>setDualLevelUp(null)}/>
      )}
      {notif&&<SysNotif msg={notif.msg} sub={notif.sub} type={notif.type} anim={notif.anim}/>}
      {/* Offline / syncing banner */}
      {(isOffline||pendingSync)&&(
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:9500,
          background:pendingSync?"rgba(56,189,248,0.95)":"rgba(30,20,0,0.97)",
          borderBottom:`1px solid ${pendingSync?"rgba(56,189,248,0.4)":"rgba(245,158,11,0.4)"}`,
          padding:"6px 16px",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          <span style={{fontSize:12}}>{pendingSync?"⟳":"⚠️"}</span>
          <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
            color:pendingSync?"#000":"#f59e0b",letterSpacing:2}}>
            {pendingSync
              ? "SYNCING OFFLINE LOGS..."
              : `OFFLINE · ${getPendingLogs(user?.id||"").length>0
                  ? `${getPendingLogs(user?.id||"").length} LOG${getPendingLogs(user?.id||"").length>1?"S":""} QUEUED`
                  : "CACHED DATA"}`}
          </span>
        </div>
      )}
      {showLevelModal&&(
        <LevelModal tasks={tasks} onUpdateXP={saveXP} onClose={()=>setShowLevelModal(false)}/>
      )}
      {showAdmin&&<AdminDashboard onClose={()=>setShowAdmin(false)}/>}
      {onboardingSkipped&&tasks.length===0&&(
        <div style={{position:"fixed",bottom:100,left:"50%",transform:"translateX(-50%)",
          background:"#030912",border:`1px solid ${SYS_BORDER}`,borderRadius:12,
          padding:"12px 18px",zIndex:7000,display:"flex",alignItems:"center",gap:12,
          maxWidth:320,width:"90%",boxShadow:"0 8px 32px rgba(0,0,0,0.8)"}}>
          <span style={{fontSize:16}}>⚡</span>
          <div style={{flex:1}}>
            <div style={{fontFamily:"monospace",fontSize:10,color:SYS,letterSpacing:1}}>SET UP YOUR FIRST TARGET</div>
            <div style={{fontSize:11,color:"#64748b",marginTop:2}}>Tap + NEW TARGET to get started</div>
          </div>
          <span onClick={()=>{AudioEngine.click();setOnboardingSkipped(false)}}
            style={{color:"#64748b",cursor:"pointer",fontSize:14}}>✕</span>
        </div>
      )}
      {showEditModal&&task&&(
        <EditTargetModal task={task} onSave={saveTaskEdit} onClose={()=>setShowEditModal(false)}/>
      )}
      {metricModal&&task&&(
        <MetricChangeModal task={task} newMetric={editingMetric}
          onKeep={()=>applyMetricChange(false)} onReset={()=>applyMetricChange(true)}
          onCancel={()=>setMetricModal(false)}/>
      )}

      <div style={{maxWidth:560,margin:"0 auto",position:"relative",
        paddingBottom:110,
        animation:dashEnter?"dashReveal 0.9s cubic-bezier(0.16,1,0.3,1) forwards":"none"}}>

        {/* ── Header ── */}
        <div style={{padding:"26px 18px 0"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
            <div style={{flex:1,minWidth:0}}>
              <div className="sys-label" style={{marginBottom:6}}>[ A·R·I·S·E ]</div>
              <div style={{fontSize:11,fontWeight:800,color:"#fff",letterSpacing:6,fontFamily:"'Share Tech Mono',monospace",marginBottom:2}}>ARISE</div>
              <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,overflow:"hidden"}}>
                {isPremium?(
                  <div onClick={()=>{AudioEngine.open();setActiveTab("settings");setSettingsPage("username");}}
                    style={{display:"flex",alignItems:"center",gap:6,position:"relative",minWidth:0,overflow:"hidden",cursor:"pointer"}}>
                    <GoldenDagger size={Math.max(20, homeNameSize(playerName)*1.0)} level={xpToLevel(calcTotalXP(tasks),calcL1Threshold(tasks)).level}/>
                    <h1 style={{
                      fontSize:homeNameSize(playerName),fontWeight:900,letterSpacing:2,
                      fontFamily:"'Share Tech Mono',monospace",whiteSpace:"nowrap",
                      margin:0,padding:0,border:"none",outline:"none",
                      overflow:"hidden",textOverflow:"ellipsis",minWidth:0,maxWidth:"100%",
                      background:"linear-gradient(90deg,#92400e,#fbbf24,#fef3c7,#fbbf24,#d97706,#fbbf24,#92400e)",
                      backgroundSize:"200% auto",
                      WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",
                      backgroundClip:"text",
                      animation:"goldShimmer 3s linear infinite",
                      position:"relative",display:"inline-block",
                    }}>
                      {playerName}
                      <span style={{position:"absolute",top:0,left:"-60%",width:"35%",height:"100%",
                        background:"linear-gradient(105deg,transparent 20%,rgba(255,249,196,0.5) 50%,transparent 80%)",
                        animation:"goldSweep 3.5s ease-in-out infinite",
                        pointerEvents:"none",WebkitBackgroundClip:"initial",backgroundClip:"initial",
                        WebkitTextFillColor:"initial"}}/>
                    </h1>
                  </div>
                ):(
                  <div onClick={()=>{AudioEngine.open();setActiveTab("settings");setSettingsPage("username");}}
                    style={{display:"flex",alignItems:"center",gap:6,minWidth:0,overflow:"hidden",cursor:"pointer"}}>
                    <GoldenDagger size={Math.max(20, homeNameSize(playerName)*1.0)} level={xpToLevel(calcTotalXP(tasks),calcL1Threshold(tasks)).level}/>
                    <h1 style={{fontSize:homeNameSize(playerName),fontWeight:800,color:SYS,letterSpacing:1,
                      fontFamily:"'Share Tech Mono',monospace",textShadow:`0 0 20px ${SYS}`,
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
                      maxWidth:"100%",margin:0}}>
                      {playerName}
                    </h1>
                  </div>
                )}
                <button onClick={()=>{AudioEngine.open();setActiveTab("settings");setSettingsPage("xp");}}
                  title="Tap to set up XP"
                  style={{background:"rgba(56,189,248,0.06)",border:`1px solid ${SYS_BORDER}`,
                    borderRadius:6,cursor:"pointer",padding:"3px 8px",lineHeight:1,flexShrink:0}}>
                  <span style={{fontFamily:"monospace",fontSize:11,color:"#fff",letterSpacing:1}}>
                    Lv.{xpToLevel(calcTotalXP(tasks),calcL1Threshold(tasks)).level}
                  </span>
                </button>
              </div>
              <div style={{fontSize:11,color:"#64748b",marginTop:3,fontFamily:"monospace"}}>
                {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
              </div>
            </div>
            {/* Right side buttons stacked */}
            <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"stretch",marginLeft:12,flexShrink:0}}>
              <button className="btn sys-btn-primary" onClick={()=>{AudioEngine.open();setShowAdd(true)}}
                style={{padding:"8px 12px",fontSize:10,textAlign:"center",lineHeight:1.3}}>
                + NEW<br/>TARGET
              </button>
              <div style={{display:"flex",gap:6}}>
                {/* Inbox bell */}
                {(()=>{
                  const unreadDirect=sysMessages.filter(m=>m.status==="unread"&&m.recipient_id===user?.id).length;
                  const unreadGlobal=sysMessages.filter(m=>m.status==="unread"&&m.recipient_id===null).length;
                  return(
                    <button onClick={()=>{
                        AudioEngine.open();
                        sysMessages.filter(m=>m.status==="unread").forEach(m=>dismissedMsgIds.current.add(m.id));
                        setShowMsgInboxAndRef(true);
                        setPendingMsg(null);
                      }}
                      title="Inbox"
                      style={{position:"relative",flex:1,background:"rgba(255,255,255,0.03)",
                        border:"1px solid rgba(255,255,255,0.06)",color:"#64748b",borderRadius:8,
                        padding:"6px 0",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                      </svg>
                      {unreadDirect>0&&(
                        <div style={{position:"absolute",top:3,right:unreadGlobal>0?12:4,width:7,height:7,
                          borderRadius:"50%",background:SYS,boxShadow:`0 0 5px ${SYS}`}}/>
                      )}
                      {unreadGlobal>0&&(
                        <div style={{position:"absolute",top:3,right:4,width:7,height:7,
                          borderRadius:"50%",background:"#f59e0b",
                          boxShadow:"0 0 5px rgba(245,158,11,0.9)"}}/>
                      )}
                    </button>
                  );
                })()}
                {isAdmin&&(
                  <button onClick={()=>{AudioEngine.open();setShowAdmin(true)}}
                    style={{flex:1,background:"rgba(255,165,0,0.1)",border:"1px solid rgba(255,165,0,0.3)",
                      color:"#f59e0b",borderRadius:8,padding:"6px 0",fontFamily:"monospace",
                      fontSize:11,cursor:"pointer",textAlign:"center"}}>
                    ⚡
                  </button>
                )}
                {/* Sign out — SVG power icon */}
                <button onClick={()=>{AudioEngine.open();setConfirmSignOut(true);}}
                  title="Sign Out"
                  style={{flex:1,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",
                    color:"#64748b",borderRadius:8,padding:"6px 0",cursor:"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </button>
              </div>{/* end bottom row */}
            </div>
            {/* Sign out confirmation overlay */}
            {confirmSignOut&&(
              <div onClick={()=>{AudioEngine.close();setConfirmSignOut(false);}}
                style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,
                  display:"flex",alignItems:"center",justifyContent:"center",padding:"0 32px"}}>
                <div onClick={e=>e.stopPropagation()}
                  style={{background:"#030912",border:`1px solid ${SYS_BORDER}`,borderRadius:16,
                    padding:"28px 24px",width:"100%",maxWidth:320,textAlign:"center",
                    boxShadow:`0 0 40px rgba(56,189,248,0.12)`}}>
                  <div style={{fontSize:32,marginBottom:12,color:"#ef4444",display:"flex",justifyContent:"center"}}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                  </div>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:13,color:SYS,
                    letterSpacing:3,marginBottom:8}}>DISCONNECT FROM SYSTEM?</div>
                  <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",marginBottom:24,lineHeight:1.5}}>
                    Your progress is saved.
                  </div>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>{AudioEngine.close();setConfirmSignOut(false);}}
                      style={{flex:1,padding:"11px",background:"rgba(255,255,255,0.04)",
                        border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                        color:"#667",fontFamily:"monospace",fontSize:11,cursor:"pointer",letterSpacing:1}}>
                      CANCEL
                    </button>
                    <button onClick={()=>{AudioEngine.error();setConfirmSignOut(false);setTimeout(()=>handleSignOut(),300);}}
                      style={{flex:1,padding:"11px",background:"rgba(239,68,68,0.12)",
                        border:"1px solid rgba(239,68,68,0.3)",borderRadius:10,
                        color:"#ef4444",fontFamily:"monospace",fontSize:11,cursor:"pointer",letterSpacing:1,fontWeight:700}}>
                      SIGN OUT
                    </button>
                  </div>
                </div>
              </div>
            )}

            {confirmQuickLog&&(
              <div onClick={()=>{AudioEngine.close();setConfirmQuickLog(null);}}
                style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,
                  display:"flex",alignItems:"center",justifyContent:"center",padding:"0 32px"}}>
                <div onClick={e=>e.stopPropagation()}
                  style={{background:"#030912",border:`1px solid ${SYS_BORDER}`,borderRadius:16,
                    padding:"28px 24px",width:"100%",maxWidth:320,textAlign:"center",
                    boxShadow:`0 0 40px rgba(56,189,248,0.12)`}}>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:13,color:SYS,
                    letterSpacing:3,marginBottom:8}}>ALREADY LOGGED TODAY</div>
                  <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",marginBottom:24,lineHeight:1.6}}>
                    You already have a log for today on <span style={{color:"#ddeeff"}}>{confirmQuickLog.title}</span>.<br/>Log one more?
                  </div>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>{AudioEngine.close();setConfirmQuickLog(null);}}
                      style={{flex:1,padding:"11px",background:"rgba(255,255,255,0.04)",
                        border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                        color:"#667",fontFamily:"monospace",fontSize:11,cursor:"pointer",letterSpacing:1}}>
                      NO
                    </button>
                    <button onClick={()=>{const t=confirmQuickLog;setConfirmQuickLog(null);doQuickLog(t);}}
                      style={{flex:1,padding:"11px",background:SYS_DIM,
                        border:`1px solid ${SYS_BORDER}`,borderRadius:10,
                        color:SYS,fontFamily:"monospace",fontSize:11,cursor:"pointer",letterSpacing:1,fontWeight:700}}>
                      YES
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete target confirmation moved outside this stacking context — see below target sheet */}
          </div>

          {/* Discovery Tip Toast — light, non-blocking, auto-dismisses */}
          {activeTip&&(
            <div style={{position:"fixed",left:16,right:16,bottom:100,zIndex:9100,
              maxWidth:520,margin:"0 auto",
              animation:"tipSlideUp 0.35s cubic-bezier(0.16,1,0.3,1) forwards"}}>
              <div style={{background:"#030912",border:`1px solid ${SYS_BORDER}`,borderRadius:14,
                boxShadow:"0 8px 30px rgba(0,0,0,0.5), 0 0 24px rgba(56,189,248,0.1)",
                padding:"12px 14px",display:"flex",alignItems:"flex-start",gap:10}}>
                <div style={{width:26,height:26,borderRadius:7,background:SYS_DIM,
                  border:`1px solid ${SYS_BORDER}`,display:"flex",alignItems:"center",
                  justifyContent:"center",fontSize:13,flexShrink:0,marginTop:1}}>
                  💡
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,
                    color:SYS,letterSpacing:2,marginBottom:3}}>[ TIP ]</div>
                  <div style={{fontFamily:"monospace",fontSize:11,color:"#ddeeff",lineHeight:1.5}}>
                    {activeTip.message}
                  </div>
                </div>
                <button onClick={()=>{setActiveTip(null);}}
                  style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",
                    fontSize:13,flexShrink:0,padding:2,lineHeight:1}}>
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* System Message Popup — shows on enter/when new msg arrives */}
          {pendingMsg&&!showMsgInbox&&(
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:9200,
              display:"flex",alignItems:"center",justifyContent:"center",padding:"0 24px"}}>
              <div style={{position:"relative",width:"100%",maxWidth:360}}>
                <div style={{position:"absolute",top:0,left:"50%",width:3,height:"100%",
                  background:pendingMsg.recipient_id===null?"#f59e0b":SYS,
                  boxShadow:`0 0 14px ${pendingMsg.recipient_id===null?"#f59e0b":SYS}, 0 0 28px ${pendingMsg.recipient_id===null?"#f59e0b":SYS}`,
                  transform:"translateX(-50%)",borderRadius:2,zIndex:1,
                  animation:"lineGlow 0.5s ease forwards"}}/>
                <div style={{background:"#030912",position:"relative",zIndex:2,
                  border:`1px solid ${pendingMsg.recipient_id===null?"rgba(245,158,11,0.35)":SYS_BORDER}`,
                  borderRadius:16,width:"100%",overflow:"hidden",
                  boxShadow:`0 0 60px ${pendingMsg.recipient_id===null?"rgba(245,158,11,0.15)":"rgba(56,189,248,0.15)"}`,
                  animation:"lineReveal 0.5s cubic-bezier(0.4,0,0.2,1) forwards"}}>
                <div style={{height:2,background:`linear-gradient(90deg,${pendingMsg.recipient_id===null?"#f59e0b":SYS},transparent)`}}/>
                <div style={{padding:"22px 20px 20px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                    <div style={{width:32,height:32,borderRadius:8,
                      background:pendingMsg.recipient_id===null?"rgba(245,158,11,0.12)":SYS_DIM,
                      border:`1px solid ${pendingMsg.recipient_id===null?"rgba(245,158,11,0.3)":SYS_BORDER}`,
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>
                      {pendingMsg.recipient_id===null?"📣":"💬"}
                    </div>
                    <div>
                      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                        color:pendingMsg.recipient_id===null?"#f59e0b":SYS,letterSpacing:3}}>
                        {pendingMsg.recipient_id===null?"[ ANNOUNCEMENT ]":"[ MESSAGE FROM ADMIN ]"}
                      </div>
                      <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",marginTop:2}}>
                        {new Date(pendingMsg.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                      </div>
                    </div>
                  </div>
                  <div style={{fontFamily:"monospace",fontSize:13,color:"#ddeeff",
                    lineHeight:1.7,marginBottom:20,whiteSpace:"pre-wrap"}}>
                    {pendingMsg.message}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{AudioEngine.click();dismissedMsgIds.current.add(pendingMsg.id);markMessageRead(pendingMsg.id);setPendingMsg(null);}}
                      style={{flex:1,padding:"10px",background:"rgba(255,255,255,0.04)",
                        border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                        color:"#64748b",fontFamily:"'Share Tech Mono',monospace",fontSize:10,cursor:"pointer",
                        letterSpacing:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      <span>✕</span> LATER
                    </button>
                    {pendingMsg.recipient_id===null?(
                      <button onClick={()=>{AudioEngine.click();dismissedMsgIds.current.add(pendingMsg.id);markMessageRead(pendingMsg.id);}}
                        style={{flex:1,padding:"10px",background:"rgba(245,158,11,0.1)",
                          border:"1px solid rgba(245,158,11,0.3)",borderRadius:10,
                          color:"#f59e0b",fontFamily:"'Share Tech Mono',monospace",fontSize:10,cursor:"pointer",
                          letterSpacing:1,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                        <span>✓</span> MARK READ
                      </button>
                    ):(
                      <button onClick={()=>{AudioEngine.click();dismissedMsgIds.current.add(pendingMsg.id);markMessageRead(pendingMsg.id);setShowMsgInboxAndRef(true);}}
                        style={{flex:1,padding:"10px",background:SYS_DIM,
                          border:`1px solid ${SYS_BORDER}`,borderRadius:10,
                          color:SYS,fontFamily:"'Share Tech Mono',monospace",fontSize:10,cursor:"pointer",
                          letterSpacing:1,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                        <span>💬</span> OPEN CHAT
                      </button>
                    )}
                  </div>
                </div>
              </div>
              </div>
            </div>
          )}

          {/* Inbox Modal */}
          {showMsgInbox&&(
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:8700,
              display:"flex",alignItems:"flex-end",justifyContent:"center",
              paddingBottom:90}}
              onClick={e=>{if(e.target===e.currentTarget){AudioEngine.close();setShowMsgInboxAndRef(false);}}}>
              <div style={{background:"#030912",border:"1px solid rgba(56,189,248,0.25)",
                borderRadius:"18px 18px 0 0",width:"100%",maxWidth:560,
                height:"calc(100vh - 160px)",
                display:"flex",flexDirection:"column",
                animation:"slideUp 0.3s cubic-bezier(.34,1.56,.64,1)"}}>

                {/* Fixed header */}
                <div style={{padding:"20px 18px 14px",flexShrink:0,
                  borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                  <div style={{height:2,background:`linear-gradient(90deg,rgba(56,189,248,0.8),transparent)`,
                    borderRadius:2,marginBottom:14}}/>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div className="sys-label" style={{marginBottom:4}}>[ INBOX ]</div>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b"}}>
                        {sysMessages.filter(m=>m.status==="unread").length} unread
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <button onClick={()=>fetchMessages(user.id)}
                        style={{background:"none",border:"none",color:"#64748b",
                          cursor:"pointer",fontSize:12,fontFamily:"monospace"}}>
                        ↻
                      </button>
                      <button onClick={()=>{AudioEngine.close();setShowMsgInboxAndRef(false);}}
                        className="sys-close" style={{fontSize:18}}>✕</button>
                    </div>
                  </div>
                </div>

                {/* Scrollable content */}
                <div style={{overflowY:"auto",flex:1,padding:"16px 18px 32px"}}>

                  {/* ── Global announcements ── */}
                  {(()=>{
                    const globalMsgs=sysMessages.filter(m=>m.recipient_id===null);
                    if(globalMsgs.length===0) return null;
                    return(
                      <div style={{marginBottom:20}}>
                        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,
                          color:"#f59e0b",letterSpacing:3,marginBottom:10}}>[ ANNOUNCEMENTS ]</div>
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {globalMsgs.map((m,i)=>(
                            <div key={i} style={{padding:"12px 14px",borderRadius:10,
                              background:m.status==="unread"?"rgba(245,158,11,0.05)":"rgba(255,255,255,0.01)",
                              border:`1px solid ${m.status==="unread"?"rgba(245,158,11,0.3)":"rgba(255,255,255,0.04)"}`,
                              position:"relative"}}>
                              {m.status==="unread"&&(
                                <div style={{position:"absolute",top:10,right:10,width:7,height:7,
                                  borderRadius:"50%",background:"#f59e0b",
                                  boxShadow:"0 0 5px rgba(245,158,11,0.9)"}}/>
                              )}
                              <div style={{fontFamily:"monospace",fontSize:9,color:"#f59e0b",
                                marginBottom:5,letterSpacing:1}}>
                                🌐 {new Date(m.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                              </div>
                              <div style={{fontFamily:"monospace",fontSize:12,
                                color:m.status==="unread"?"#ddeeff":"#667",
                                lineHeight:1.7,whiteSpace:"pre-wrap",marginBottom:8}}>
                                {m.message}
                              </div>
                              {m.status==="unread"&&(
                                <button onClick={()=>{AudioEngine.click();dismissedMsgIds.current.add(m.id);markMessageRead(m.id);}}
                                  style={{padding:"5px 12px",background:"rgba(245,158,11,0.1)",
                                    border:"1px solid rgba(245,158,11,0.3)",borderRadius:6,
                                    color:"#f59e0b",fontFamily:"'Share Tech Mono',monospace",
                                    fontSize:9,cursor:"pointer",letterSpacing:1}}>
                                  ✓ MARK READ
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Admin chat thread ── */}
                  {(()=>{
                    const directMsgs=sysMessages.filter(m=>m.recipient_id===user?.id);
                    const thread=[
                      ...directMsgs.map(m=>({type:"admin",text:m.message,date:m.created_at,id:m.id,status:m.status})),
                      ...userReplies.map(r=>({type:"user",text:r.reply,date:r.created_at,id:r.id})),
                    ].sort((a,b)=>new Date(a.date)-new Date(b.date));

                    return(
                      <div>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                          padding:"12px 14px 8px",flexShrink:0}}>
                          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,
                            color:SYS,letterSpacing:3}}>[ MESSAGES ]</div>
                        </div>
                        <div style={{background:"rgba(255,255,255,0.02)",border:`1px solid ${SYS_BORDER}`,
                          borderRadius:12,overflow:"hidden"}}>
                          <div style={{padding:"12px",display:"flex",flexDirection:"column",gap:8}}>
                            {thread.length===0&&(
                              <div style={{textAlign:"center",padding:"20px 0",
                                fontFamily:"monospace",fontSize:11,color:"#64748b"}}>
                                No messages yet
                              </div>
                            )}
                            {thread.map((item,i)=>(
                              <div key={i} style={{display:"flex",
                                justifyContent:item.type==="admin"?"flex-start":"flex-end"}}>
                                <div style={{maxWidth:"85%",padding:"9px 12px",borderRadius:10,
                                  background:item.type==="admin"?"rgba(56,189,248,0.08)":"rgba(74,222,128,0.07)",
                                  border:`1px solid ${item.type==="admin"?"rgba(56,189,248,0.15)":"rgba(74,222,128,0.15)"}`}}>
                                  <div style={{fontFamily:"monospace",fontSize:9,
                                    color:item.type==="admin"?SYS:"#4ade80",
                                    marginBottom:4,letterSpacing:1}}>
                                    {item.type==="admin"?"ADMIN":"YOU"}
                                  </div>
                                  <div style={{fontFamily:"monospace",fontSize:12,color:"#ddeeff",
                                    lineHeight:1.6,whiteSpace:"pre-wrap"}}>
                                    {item.text.replace(/^\[CONTACT US\]\s*/,"")}
                                  </div>
                                  <div style={{fontFamily:"monospace",fontSize:8,color:"#64748b",marginTop:3,
                                    display:"flex",gap:6,alignItems:"center"}}>
                                    {new Date(item.date).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                                    {item.type==="admin"&&item.status==="unread"&&(
                                      <span style={{color:"#ef4444"}}>● new</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* Go to Contact Us button */}
                          <div style={{padding:"10px 12px",borderTop:"1px solid rgba(255,255,255,0.05)",
                            background:"rgba(0,0,0,0.15)"}}>
                            <button onClick={()=>{
                                AudioEngine.click();
                                setShowMsgInboxAndRef(false);
                                setActiveTab("settings");
                                setSettingsPage("contact");
                              }}
                              style={{width:"100%",padding:"9px",background:"rgba(56,189,248,0.06)",
                                border:`1px solid ${SYS_BORDER}`,borderRadius:8,
                                color:SYS,fontFamily:"'Share Tech Mono',monospace",
                                fontSize:10,letterSpacing:2,cursor:"pointer"}}>
                              ✏️ REPLY
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Period tabs */}
          <div style={{display:"flex",gap:3,background:"rgba(56,189,248,0.04)",borderRadius:10,padding:3,border:`1px solid ${SYS_BORDER}`,marginBottom:20}}>
            {PERIODS.map(p=>(
              <button key={p} className="ptab" onClick={()=>{AudioEngine.click();setCardPeriod(p)}} style={{flex:1,
                background:cardPeriod===p?SYS_DIM:"transparent",
                color:cardPeriod===p?SYS:"#64748b"}}>
                {p}
              </button>
            ))}
          </div>

          {/* ── Dashboard ── */}
          {dashTasks.length>0&&(
            <div className="sys-panel" style={{marginBottom:24}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
                <div>
                  <div className="sys-label" style={{marginBottom:4}}>[ {PERIOD_LABELS[cardPeriod].toUpperCase()} STATUS ]</div>
                  <div style={{fontSize:11,color:"#64748b",fontFamily:"monospace"}}>
                    {cardPeriod!=="daily"?`Day ${totalDays-daysLeft} of ${totalDays} · ${daysLeft}d remaining`:"Resets at midnight"}
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:20,fontWeight:800,
                    color:dashDone===dashTasks.length?"#4ade80":SYS}}>
                    {dashDone}<span style={{color:"#64748b",fontSize:14}}>/{dashTasks.length}</span>
                  </div>
                  <div style={{fontSize:10,color:"#64748b",fontFamily:"monospace"}}>TARGETS COMPLETE</div>
                </div>
              </div>
              {dashTasks.map(t=><DashRow key={t.id} task={t} period={cardPeriod} onClick={()=>{AudioEngine.open();openTask(t)}}/>)}
            </div>
          )}
        </div>

        {/* ── Target List ── */}
        <div style={{padding:"0 18px 90px"}}>
          {/* Category filter */}
          <div style={{display:"flex",gap:5,overflowX:"auto",paddingBottom:4,marginBottom:20}}>
            {[{id:"all",label:"ALL",color:SYS},...Object.entries(CAT).map(([id,c])=>({id,label:c.label.toUpperCase(),color:c.color}))].map(f=>(
              <button key={f.id} className="btn" onClick={()=>{AudioEngine.click();setFilterCat(f.id)}}
                style={{padding:"5px 11px",fontSize:10,whiteSpace:"nowrap",flexShrink:0,
                  fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,
                  background:filterCat===f.id?`${f.color}18`:"rgba(255,255,255,0.02)",
                  color:filterCat===f.id?f.color:"#64748b",
                  border:`1px solid ${filterCat===f.id?f.color+"44":"rgba(255,255,255,0.05)"}`}}>
                {f.id==="all"?"ALL":CAT[f.id]?.icon+" "+f.label}
              </button>
            ))}
          </div>

          <CardScroller filtered={filtered} cardPeriod={cardPeriod} openTask={openTask} quickLog={quickLog}/>
        </div>

        {/* ── Stats Charts ── */}
        <StatsCharts tasks={tasks} userCreatedAt={user?.created_at}/>
      </div>

      {/* ── Target Sheet ── */}
      {task&&(
        <div className="overlay" onClick={e=>{if(e.target===e.currentTarget){AudioEngine.close();closeSheet();}}}>
          <div className="sheet">
            {/* Header bar */}
            <div style={{padding:"22px 18px 0"}}>
              <div style={{height:2,background:`linear-gradient(90deg,${CAT[task.cat].color},${SYS},transparent)`,borderRadius:2,marginBottom:14}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{flex:1}}>
                  <div className="sys-label" style={{marginBottom:5}}>[ TARGET FILE ]</div>
                  <h2 style={{fontSize:18,fontWeight:800,color:"#ddeeff",letterSpacing:.3,marginBottom:4}}>{task.title}</h2>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                    <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,letterSpacing:2,
                      color:CAT[task.cat].color,background:`${CAT[task.cat].color}15`,
                      padding:"3px 9px",borderRadius:5,border:`1px solid ${CAT[task.cat].color}33`}}>
                      {CAT[task.cat].icon} {CAT[task.cat].label.toUpperCase()}
                    </span>
                    <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,letterSpacing:2,
                      color:SCOL[task.status],background:`${SCOL[task.status]}15`,
                      padding:"3px 9px",borderRadius:5,border:`1px solid ${SCOL[task.status]}30`}}>
                      {task.status.toUpperCase()}
                    </span>
                    <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,letterSpacing:1,
                      color:"#64748b",background:"rgba(255,255,255,0.03)",
                      padding:"3px 9px",borderRadius:5,border:"1px solid rgba(255,255,255,0.07)"}}>
                      {METRICS[task.metric]?.icon} {METRICS[task.metric]?.label}
                    </span>
                  </div>
                </div>
                {/* Action icons */}
                <div style={{display:"flex",gap:4,alignItems:"center",marginLeft:10,flexShrink:0}}>
                  <button title="Edit target" onClick={(e)=>{e.stopPropagation();AudioEngine.open();setShowEditModal(true)}}
                    style={{background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,color:SYS,
                      borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:15,
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                    ✏️
                  </button>
                  <button title="Delete target" onClick={(e)=>{e.stopPropagation();AudioEngine.click();setConfirmDel(true)}}
                      style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",color:WARN,
                        borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:15,
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                      🗑
                    </button>
                  <button onClick={()=>{AudioEngine.close();closeSheet();}}
                    style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",color:"#64748b",
                      borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:14,
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                    ✕
                  </button>
                </div>
              </div>
              <p style={{fontSize:12,color:"#64748b",lineHeight:1.5,marginBottom:20}}>{task.desc||""}</p>
            </div>

            <div style={{padding:"0 18px"}}>
              {/* Metric editor */}
              {showMetricEdit&&(
                <div className="sys-panel" style={{marginBottom:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div className="sys-label">[ CHANGE METRIC ]</div>
                    <button onClick={()=>{AudioEngine.close();setShowMetricEdit(false);}} className="sys-close">✕</button>
                  </div>
                  <MetricPicker value={editingMetric} customUnit={pendingUnit} onChangeMetric={setEditingMetric} onChangeUnit={setPendingUnit}/>
                  <button className="btn sys-btn-primary" onClick={()=>{AudioEngine.click();requestMetricChange()}} style={{marginTop:12,width:"100%",padding:"10px"}}>[ APPLY ]</button>
                </div>
              )}

              {/* Period progress */}
              <div className="sys-panel" style={{marginBottom:24}}>
                <div style={{display:"flex",gap:3,background:"rgba(0,0,0,0.3)",borderRadius:7,padding:3,marginBottom:24}}>
                  {PERIODS.map(p=>(
                    <button key={p} className="ptab" onClick={()=>{AudioEngine.click();setSheetPeriod(p)}} style={{flex:1,
                      background:sheetPeriod===p?SYS_DIM:"transparent",color:sheetPeriod===p?SYS:"#64748b",fontSize:9,padding:"4px 6px"}}>
                      {p.toUpperCase()}
                    </button>
                  ))}
                </div>
                {(()=>{
                  const {achieved,goalAmt,pct,remaining,overage,hasGoal,met,behind,isDerived,source}=getPeriodProgress(task,sheetPeriod);
                  const tdays=realDaysInPeriod(sheetPeriod);
                  const dleft=daysLeftInPeriod(sheetPeriod);
                            return(
                    <>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <div style={{display:"flex",alignItems:"center",gap:7}}>
                          <span className="sys-label">{PERIOD_LABELS[sheetPeriod].toUpperCase()}</span>
                          {isDerived&&<span style={{fontSize:9,color:"#64748b",fontFamily:"monospace"}}>from {source}</span>}
                          {met&&<span style={{fontSize:9,color:"#4ade80",fontFamily:"monospace",letterSpacing:1}}>COMPLETE</span>}
                          {behind&&<span style={{fontSize:9,color:WARN,fontFamily:"monospace",letterSpacing:1}}>⚠ BEHIND</span>}
                        </div>
                        <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:13,fontWeight:700,color:met?"#4ade80":behind?WARN:SYS}}>
                          {fmtAmount(achieved,task.metric,task.customUnit)}{hasGoal?` / ${fmtAmount(goalAmt,task.metric,task.customUnit)}`:""}
                        </span>
                      </div>
                      {hasGoal&&(
                        <div className="ptrack" style={{marginBottom:8}}>
                          <div className="pfill" style={{width:`${pct||0}%`,
                            background:met?"linear-gradient(90deg,#4ade80,#22d3ee)":behind?`linear-gradient(90deg,${WARN},#f97316)`:`linear-gradient(90deg,${SYS},${SYS2})`}}/>
                        </div>
                      )}
                      <div style={{display:"flex",justifyContent:"space-between",fontFamily:"monospace",fontSize:10,color:"#64748b"}}>
                        <span style={{color:met?"#4ade80":behind?WARN:"#64748b"}}>
                          {!hasGoal
                            ?`${METRICS[task.metric]?.icon} ${fmtAmount(task.total,task.metric,task.customUnit)} all time`
                            :met
                              ?overage>0?`+${fmtAmount(overage,task.metric,task.customUnit)} exceeded`:"target met"
                              :`${fmtAmount(remaining,task.metric,task.customUnit)} remaining`
                          }
                        </span>
                        {sheetPeriod!=="daily"&&<span>Day {tdays-dleft}/{tdays} · {dleft}d left</span>}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Report form */}
              <div className="sys-panel" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <div className="sys-label">[ FILE REPORT — {METRICS[task.metric]?.label.toUpperCase()} ]</div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:1}}>DATE</span>
                    <input type="date" value={lDate} onChange={e=>setLDate(e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                      style={{background:"rgba(56,189,248,0.06)",border:"1px solid rgba(56,189,248,0.2)",
                        borderRadius:7,color:lDate?"#38bdf8":"#94a3b8",padding:"4px 8px",fontSize:11,
                        fontFamily:"monospace",outline:"none",cursor:"pointer"}}/>
                    {lDate&&<button onClick={()=>{AudioEngine.click();setLDate("")}}
                      style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:12,padding:"0 2px"}}>✕</button>}
                  </div>
                </div>
                <div style={{marginBottom:14}}><MetricInput metric={task.metric} customUnit={task.customUnit} value={lVal} onChange={setLVal}/></div>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:6}}>STATUS</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                  {LOG_STATUSES.map(s=>(
                    <button key={s} className="btn" onClick={()=>{ AudioEngine.click(); setLStatus(s); if(s==="Done"){ setLProg("100"); AudioEngine.complete(); } }}
                      style={{padding:"5px 10px",fontSize:10,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,
                        background:lStatus===s?`${SCOL[s]}20`:"rgba(255,255,255,0.02)",
                        color:lStatus===s?SCOL[s]:"#64748b",
                        border:`1px solid ${lStatus===s?SCOL[s]+"50":"rgba(255,255,255,0.06)"}`}}>
                      {s.toUpperCase()}
                    </button>
                  ))}
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2}}>COMPLETION</span>
                  <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,fontWeight:700,
                    color:lStatus==="Done"?"#4ade80":lStatus==="Not Started"?"#94a3b8":SYS}}>
                    {lStatus==="Done"?"✓ 100%":`${lProg}%`}
                  </span>
                </div>
                {lStatus==="Done"?(
                  <div style={{position:"relative",height:6,borderRadius:4,background:"rgba(255,255,255,0.06)",marginBottom:10,overflow:"hidden"}}>
                    <div style={{position:"absolute",inset:0,borderRadius:4,transformOrigin:"left",
                      background:"linear-gradient(90deg,#4ade80,#86efac,#4ade80)",backgroundSize:"200% auto",
                      animation:"completeFill 0.6s cubic-bezier(0.22,1,0.36,1) forwards, shimmer 1.8s linear 0.6s infinite"}}/>
                  </div>
                ):(
                  <input type="range" min="0" max="100" value={lProg}
                    onChange={e=>{ if(lStatus==="In Progress"||lStatus==="Review") setLProg(e.target.value); }}
                    disabled={lStatus==="Not Started"}
                    style={{width:"100%",accentColor:SYS,marginBottom:10,
                      cursor:lStatus==="Not Started"?"not-allowed":"pointer",
                      opacity:lStatus==="Not Started"?0.25:1}}/>
                )}
                <textarea className="field" rows={2} placeholder="Additional notes..." value={lNote}
                  onChange={e=>setLNote(e.target.value)} style={{resize:"none",marginBottom:16,fontSize:13}}/>
                <button className="btn sys-btn-primary" onClick={()=>{AudioEngine.log();submitLog()}} style={{width:"100%",padding:"13px",fontSize:12}}>
                  [ SUBMIT REPORT — {task.title.toUpperCase()} ]
                </button>
              </div>

              {/* Report history */}
              {task.logs.length>0&&(
                <div style={{marginBottom:8}}>
                  <div className="sys-label" style={{marginBottom:10}}>[ REPORT HISTORY ]</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {task.logs.map(log=>{
                      const isEdit=editId===log.id;
                      const lm=log.metric||task.metric;
                      return(
                        <div key={log.id} className="log-entry"
                          style={{background:"rgba(56,189,248,0.03)",border:`1px solid ${SYS_BORDER}`,borderRadius:9,padding:"10px 12px"}}>
                          {isEdit?(
                            <div>
                              <div style={{marginBottom:8}}><MetricInput metric={task.metric} customUnit={task.customUnit} value={eVal} onChange={setEVal} compact/></div>
                              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:8}}>
                                {LOG_STATUSES.map(s=>(
                                  <button key={s} className="btn" onClick={()=>{ AudioEngine.click(); setEStatus(s); if(s==="Done"){ setEProg("100"); AudioEngine.complete(); } }}
                                    style={{padding:"4px 8px",fontSize:10,fontFamily:"monospace",
                                      background:eStatus===s?`${SCOL[s]}20`:"rgba(255,255,255,0.02)",
                                      color:eStatus===s?SCOL[s]:"#64748b",
                                      border:`1px solid ${eStatus===s?SCOL[s]+"50":"rgba(255,255,255,0.06)"}`}}>
                                    {s.toUpperCase()}
                                  </button>
                                ))}
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between",fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:3}}>
                                <span>COMPLETION</span>
                                <span style={{fontWeight:700,
                                  color:eStatus==="Done"?"#4ade80":eStatus==="Not Started"?"#94a3b8":SYS}}>
                                  {eStatus==="Done"?"✓ 100%":`${eProg}%`}
                                </span>
                              </div>
                              {eStatus==="Done"?(
                                <div style={{position:"relative",height:6,borderRadius:4,background:"rgba(255,255,255,0.06)",marginBottom:8,overflow:"hidden"}}>
                                  <div style={{position:"absolute",inset:0,borderRadius:4,transformOrigin:"left",
                                    background:"linear-gradient(90deg,#4ade80,#86efac,#4ade80)",backgroundSize:"200% auto",
                                    animation:"completeFill 0.6s cubic-bezier(0.22,1,0.36,1) forwards, shimmer 1.8s linear 0.6s infinite"}}/>
                                </div>
                              ):(
                                <input type="range" min="0" max="100" value={eProg}
                                  onChange={e=>{ if(eStatus==="In Progress"||eStatus==="Review") setEProg(e.target.value); }}
                                  disabled={eStatus==="Not Started"}
                                  style={{width:"100%",accentColor:SYS,marginBottom:8,
                                    cursor:eStatus==="Not Started"?"not-allowed":"pointer",
                                    opacity:eStatus==="Not Started"?0.25:1}}/>
                              )}
                              <textarea className="field" rows={2} value={eNote} onChange={e=>setENote(e.target.value)} style={{resize:"none",fontSize:13,marginBottom:8}}/>
                              <div style={{display:"flex",gap:7}}>
                                <button className="btn sys-btn-primary" onClick={()=>{AudioEngine.click();saveEdit()}} style={{flex:1,padding:"8px",fontSize:11}}>[ SAVE ]</button>
                                <button className="btn" onClick={()=>{AudioEngine.close();setEditId(null)}} style={{padding:"8px 12px",background:"rgba(255,255,255,0.03)",color:"#64748b",border:"1px solid rgba(255,255,255,0.06)",fontSize:11}}>CANCEL</button>
                              </div>
                            </div>
                          ):(
                            <>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:log.note?5:0}}>
                                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                                  <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,color:SYS,fontWeight:600}}>
                                    {METRICS[lm]?.icon} {lm==="time"?fmtMins(log.value?.totalMins||0):fmtAmount(log.value?.amount||0,lm,task.customUnit)}
                                  </span>
                                  <span style={{fontFamily:"monospace",fontSize:9,letterSpacing:1,
                                    color:SCOL[log.status],background:`${SCOL[log.status]}15`,
                                    padding:"2px 7px",borderRadius:4,border:`1px solid ${SCOL[log.status]}30`}}>
                                    {log.status.toUpperCase()}
                                  </span>
                                  <span style={{fontFamily:"monospace",fontSize:10,color:"#64748b"}}>{log.progress}%</span>
                                </div>
                                <div style={{display:"flex",gap:5,alignItems:"center",flexShrink:0}}>
                                  <span style={{fontSize:9,color:"#64748b",fontFamily:"monospace"}}>{fmtDate(log.date)}</span>
                                  <button onClick={()=>{AudioEngine.click();startEdit(log)}} style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:12,padding:"2px"}}>✏️</button>
                                  <button onClick={()=>{AudioEngine.click();deleteLog(log.id)}} style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:12,padding:"2px"}}>🗑</button>
                                </div>
                              </div>
                              {log.note&&<p style={{fontSize:11,color:"#64748b",lineHeight:1.4,marginTop:3,fontFamily:"monospace"}}>{log.note}</p>}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete target confirmation overlay — rendered at root level (sibling of target sheet)
          so its z-index isn't trapped inside the dashboard's stacking context */}
      {confirmDel&&(
        <div onClick={()=>{AudioEngine.close();setConfirmDel(false);}}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,
            display:"flex",alignItems:"center",justifyContent:"center",padding:"0 32px"}}>
          <div style={{position:"relative",width:"100%",maxWidth:320}}>
            <div style={{position:"absolute",top:0,left:"50%",width:3,height:"100%",
              background:"#ef4444",boxShadow:"0 0 14px #ef4444, 0 0 28px #ef4444",
              transform:"translateX(-50%)",borderRadius:2,zIndex:1,
              animation:"lineGlow 0.5s ease forwards"}}/>
            <div onClick={e=>e.stopPropagation()} className="sys-notif-card"
              style={{background:"#030912",border:"1px solid rgba(239,68,68,0.3)",borderRadius:16,
                padding:"28px 24px",width:"100%",textAlign:"center",position:"relative",zIndex:2,
                boxShadow:"0 0 40px rgba(239,68,68,0.15)",
                animation:"lineReveal 0.5s cubic-bezier(0.4,0,0.2,1) forwards"}}>
              <div style={{fontSize:32,marginBottom:12,color:"#ef4444",display:"flex",justifyContent:"center"}}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                </svg>
              </div>
              <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:13,color:"#ef4444",
                letterSpacing:2,marginBottom:8}}>DELETE TARGET?</div>
              <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",marginBottom:24,lineHeight:1.6}}>
                You are deleting a target. This will delete <span style={{color:"#ddeeff"}}>all logs and XP</span> tied to it. Do you want to proceed?
              </div>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>{AudioEngine.close();setConfirmDel(false);}}
                  style={{flex:1,padding:"11px",background:"rgba(255,255,255,0.04)",
                    border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                    color:"#667",fontFamily:"monospace",fontSize:11,cursor:"pointer",letterSpacing:1}}>
                  CANCEL
                </button>
                <button onClick={()=>{AudioEngine.error();setConfirmDel(false);deleteTask();}}
                  style={{flex:1,padding:"11px",background:"rgba(239,68,68,0.12)",
                    border:"1px solid rgba(239,68,68,0.3)",borderRadius:10,
                    color:"#ef4444",fontFamily:"monospace",fontSize:11,cursor:"pointer",letterSpacing:1,fontWeight:700}}>
                  DELETE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── New Target Sheet ── */}
      {showAdd&&(
        <div className="overlay" onClick={e=>{if(e.target===e.currentTarget){AudioEngine.close();setShowAdd(false);}}}>
          <div className="sheet">
            <div style={{padding:"24px 18px 0"}}>
              <div style={{height:2,background:`linear-gradient(90deg,${SYS},transparent)`,borderRadius:2,marginBottom:14}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                <div>
                  <div className="sys-label" style={{marginBottom:4}}>[ REGISTER NEW TARGET ]</div>
                </div>
                <button onClick={()=>{AudioEngine.close();setShowAdd(false)}} className="sys-close" style={{fontSize:18}}>✕</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:6}}>TARGET NAME *</div>
                  <input className="field" placeholder="Enter target name..." value={nTitle} onChange={e=>setNTitle(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTask()}/>
                </div>
                <div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:6}}>DESCRIPTION</div>
                  <textarea className="field" rows={2} placeholder="Objective details..." value={nDesc} onChange={e=>setNDesc(e.target.value)} style={{resize:"none"}}/>
                </div>
                <div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:8}}>CATEGORY</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {Object.entries(CAT).map(([id,c])=>(
                      <button key={id} className="btn" onClick={()=>{AudioEngine.click();setNCat(id)}}
                        style={{padding:"6px 11px",fontSize:11,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,
                          background:nCat===id?`${c.color}18`:"rgba(255,255,255,0.02)",
                          color:nCat===id?c.color:"#64748b",
                          border:`1px solid ${nCat===id?c.color+"44":"rgba(255,255,255,0.06)"}`}}>
                        {c.icon} {c.label.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:8}}>TRACKING METRIC</div>
                  <MetricPicker value={nMetric} customUnit={nCustomUnit} onChangeMetric={setNMetric} onChangeUnit={setNCustomUnit}/>
                </div>
                <div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:8}}>TARGET PARAMETERS (optional)</div>
                  <div style={{display:"flex",flexDirection:"column",gap:7}}>
                    {PERIODS.map(p=>{
                      const g=nGoals[p];
                      const active=!!g&&(nMetric==="time"?(parseInt(g.h||0)+parseInt(g.m||0))>0:parseFloat(g.amount||0)>0);
                      const previewTask={metric:nMetric,customUnit:nCustomUnit,goals:nGoals,logs:[]};
                      const derived=active?0:getEffectiveGoalAmt(previewTask,p);
                      const src=getGoalSource(previewTask,p);
                      return(
                        <div key={p} className={active?"sys-row-active":"sys-row"}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:active?8:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,letterSpacing:2,color:active?SYS:"#64748b",textTransform:"uppercase"}}>{p}</span>
                              {!active&&derived>0&&<span style={{fontSize:10,color:"#64748b",fontFamily:"monospace"}}>→ {fmtAmount(derived,nMetric,nCustomUnit)} from {src}</span>}
                              {active&&<span className="sys-tag">SET</span>}
                            </div>
                            {!active
                              ?<button className="btn sys-btn-sm" onClick={()=>setNGoals(prev=>({...prev,[p]:nMetric==="time"?{h:"",m:""}:{amount:""}}))}>+ Set</button>
                              :<button className="btn sys-btn-sm warn" onClick={()=>setNGoals(prev=>{const n={...prev};delete n[p];return n;})}>Remove</button>
                            }
                          </div>
                          {g!==undefined&&<MetricInput metric={nMetric} customUnit={nCustomUnit} value={g||{}} onChange={v=>setNGoals(prev=>({...prev,[p]:v}))} compact/>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button className="btn sys-btn-primary" onClick={()=>{AudioEngine.click();addTask()}} style={{padding:"13px",fontSize:12,marginTop:2}}>
                  [ REGISTER TARGET ]
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Bottom Nav ── */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:7500,
        display:"flex",justifyContent:"center"}}>
        <div style={{width:"100%",maxWidth:560,
          background:"rgba(2,6,14,0.97)",
          borderTop:"1px solid rgba(56,189,248,0.15)",
          display:"flex",alignItems:"center",justifyContent:"space-around",
          padding:"10px 32px 28px",position:"relative"}}>

          {/* Left — Ranks */}
          {(()=>{const active=activeTab==="ranks"; return(
            <button onClick={()=>{AudioEngine.open();setActiveTab("ranks");}}
              style={{background:"none",border:"none",cursor:"pointer",
                display:"flex",flexDirection:"column",alignItems:"center",gap:4,
                padding:"4px 16px",position:active?"relative":"static",top:active?-22:0,
                transition:"all 0.3s cubic-bezier(0.34,1.56,0.64,1)"}}>
              {active?(
                <div style={{width:56,height:56,borderRadius:"50%",
                  background:"radial-gradient(circle at 40% 35%,#1e3a5f,#0a1628)",
                  boxShadow:"0 0 0 1px rgba(56,189,248,0.3), 0 0 20px rgba(56,189,248,0.2), 0 8px 24px rgba(0,0,0,0.8)",
                  display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                  gap:2,position:"relative",overflow:"hidden"}}>
                  <div style={{position:"absolute",inset:3,borderRadius:"50%",border:"1px solid rgba(56,189,248,0.25)"}}/>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{filter:"drop-shadow(0 0 6px rgba(56,189,248,0.8))"}}>
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                    <path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                  </svg>
                </div>
              ):(
                <>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                    <path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                  </svg>
                  <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:8,letterSpacing:2,color:"#64748b"}}>RANKS</span>
                </>
              )}
            </button>
          );})()}

          {/* Center — Home */}
          {(()=>{const active=activeTab==="home"; return(
            <div style={{position:"relative",top:active?-22:-4,
              transition:"all 0.3s cubic-bezier(0.34,1.56,0.64,1)"}}>
              <button onClick={()=>{AudioEngine.close();setActiveTab("home");setShowMsgInboxAndRef(false);}}
                style={{width:active?64:48,height:active?64:48,borderRadius:"50%",cursor:"pointer",border:"none",
                  background:active?"radial-gradient(circle at 40% 35%,#1e3a5f,#0a1628)":"rgba(255,255,255,0.04)",
                  boxShadow:active?"0 0 0 1px rgba(56,189,248,0.3), 0 0 20px rgba(56,189,248,0.2), 0 8px 24px rgba(0,0,0,0.8)":"none",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  position:"relative",overflow:"hidden",
                  transition:"all 0.3s cubic-bezier(0.34,1.56,0.64,1)"}}>
                {active&&<div style={{position:"absolute",inset:4,borderRadius:"50%",border:"1px solid rgba(56,189,248,0.25)"}}/>}
                <svg width={active?26:20} height={active?26:20} viewBox="0 0 24 24" fill="none"
                  stroke={active?"#38bdf8":"#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={active?{filter:"drop-shadow(0 0 6px rgba(56,189,248,0.8))"}:{}}>
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </button>
              {!active&&<span style={{display:"block",textAlign:"center",fontFamily:"'Share Tech Mono',monospace",
                fontSize:8,letterSpacing:2,color:"#64748b",marginTop:4}}>HOME</span>}
            </div>
          );})()}

          {/* Right — Settings */}
          {(()=>{const active=activeTab==="settings"; return(
            <button onClick={()=>{AudioEngine.open();setActiveTab("settings");setSettingsPage(null);}}
              style={{background:"none",border:"none",cursor:"pointer",
                display:"flex",flexDirection:"column",alignItems:"center",gap:4,
                padding:"4px 16px",position:active?"relative":"static",top:active?-22:0,
                transition:"all 0.3s cubic-bezier(0.34,1.56,0.64,1)"}}>
              {active?(
                <div style={{width:56,height:56,borderRadius:"50%",
                  background:"radial-gradient(circle at 40% 35%,#1e3a5f,#0a1628)",
                  boxShadow:"0 0 0 1px rgba(56,189,248,0.3), 0 0 20px rgba(56,189,248,0.2), 0 8px 24px rgba(0,0,0,0.8)",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  position:"relative",overflow:"hidden"}}>
                  <div style={{position:"absolute",inset:3,borderRadius:"50%",border:"1px solid rgba(56,189,248,0.25)"}}/>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{filter:"drop-shadow(0 0 6px rgba(56,189,248,0.8))"}}>
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
              ):(
                <>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:8,letterSpacing:2,color:"#64748b"}}>SETTINGS</span>
                </>
              )}
            </button>
          );})()}
        </div>
      </div>

      {/* ── Ranks Tab Content ── */}
      {activeTab==="ranks"&&(
        <div style={{position:"fixed",inset:0,bottom:80,zIndex:7400,
          background:"#02060e",overflowY:"auto"}}>
          <GlobalRanking
            user={user}
            tasks={tasks}
            playerName={playerName}
            isPremium={isPremium}
            globalOptedIn={globalOptedIn}
            onOptInSaved={()=>setGlobalOptedIn(true)}
            onTasksAdded={(newTasks)=>{
              setTasks(prev=>{
                const updated=[...prev,...newTasks];
                newTasks.forEach(t=>saveTask(user.id,t));
                return updated;
              });
            }}
          />
        </div>
      )}

      {/* ── Settings Tab Content ── */}
      {activeTab==="settings"&&(
        <div style={{position:"fixed",inset:0,bottom:80,zIndex:7400,
          background:"#02060e",overflowY:"auto"}}>
          <div style={{maxWidth:560,width:"100%",margin:"0 auto",padding:"24px 16px 32px"}}>

            {/* ── Settings Sub-pages ── */}
            {settingsPage&&(
              <button onClick={()=>{AudioEngine.close();setSettingsPage(null);}}
                style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",
                  fontFamily:"monospace",fontSize:12,marginBottom:20,padding:0,display:"flex",
                  alignItems:"center",gap:6}}>
                ← Back
              </button>
            )}

            {/* ── Change Username ── */}
            {settingsPage==="username"&&(()=>{
              return <SettingsUsernamePage
                playerName={playerName} userId={user?.id} isPremium={isPremium}
                onRename={async(newName)=>{
                  const clean=sanitizeName(newName);
                  await saveProfile(user.id,clean,user.email);
                  setPlayerName(clean);
                }}
              />;
            })()}

        {/* ── Setup XP ── */}
            {settingsPage==="xp"&&(
              <SettingsXPPage tasks={tasks} calcL1Threshold={calcL1Threshold} calcTotalXP={calcTotalXP} xpToLevel={xpToLevel} onSave={(xpMap)=>{
                setTasks(prev=>prev.map(t=>({...t,xp:parseInt(xpMap[t.id])||DEFAULT_XP})));
                setSettingsPage(null);
              }}/>
            )}
            {/* ── Audio ── */}
            {settingsPage==="audio"&&(
              <SettingsAudioPage />
            )}
            {/* ── Notifications ── */}
            {settingsPage==="notifications"&&(
              <SettingsNotificationsPage userId={user?.id} />
            )}
            {/* ── How to get premium username ── */}
            {settingsPage==="premium-info"&&(
              <SettingsInfoPage title="PREMIUM USERNAMES" label="[ HOW TO GET ONE ]">
                <p>Premium usernames are exclusive names from the ARISE universe — legendary hunter names, guild titles, and rare monikers that set you apart from other players.</p>
                <h4>What makes a name premium?</h4>
                <p>Premium names display with a <strong>golden shimmer animation</strong>, an <strong>animated sword prefix</strong>, and a <strong>gold awakening screen</strong> when you open the app. In the global leaderboard, your name glows gold and stands out among all players.</p>
                <h4>Two types of premium names</h4>
                <p><strong>Reserved names</strong> — names already in the ARISE premium list (legendary hunters, guild names, etc.). These can be requested if unclaimed.</p>
                <p><strong>Custom names</strong> — any name you want that isn't already taken. Submit a request and the admin will review it for approval.</p>
                <h4>How to request</h4>
                <p>1. Go to <strong>Settings → Contact Us</strong> or use your Inbox</p>
                <p>2. Send a message with your desired name and a note</p>
                <p>3. Complete payment as instructed by admin</p>
                <p>4. Once confirmed, your name will be updated and premium status granted</p>
                <h4>Can I request any name?</h4>
                <p>Yes — you can request any name that isn't already taken by another player. The admin reviews all requests and has final approval.</p>
              </SettingsInfoPage>
            )}

            {/* ── How ARISE works ── */}
            {settingsPage==="how-arise"&&(
              <SettingsInfoPage title="HOW ARISE WORKS" label="[ SYSTEM GUIDE ]">
                <p>ARISE is a Solo Leveling-themed productivity tracker. Every real-world effort you make earns XP and levels up your player — just like a hunter rising through the ranks.</p>
                <h4>Targets</h4>
                <p>Targets are your ongoing goals — things you want to track regularly. Each target has a category (Work, Health, Learning, Creative, Personal, Other), a metric (time, pages, reps, distance, words, or custom), and optional weekly/daily/monthly goals.</p>
                <h4>Reports (Logs)</h4>
                <p>Every time you make progress on a target, you file a Report. Reports record what you did, the status, completion percentage, and an optional note. Your XP is calculated from these reports.</p>
                <h4>XP & Leveling</h4>
                <p>Each log earns XP proportionally: <strong>(log value ÷ weekly goal) × weekly XP</strong>. The more consistently you hit your goals, the faster you level up. Every level costs the same — hit your weekly goals and you level up once per week.</p>
                <h4>Categories</h4>
                <p>Work, Personal, Health, Learning, Creative, and Other. Targets are color-coded by category so you can see at a glance where your effort is going.</p>
                <h4>Dashboard</h4>
                <p>Your main screen shows all active targets as scrollable cards. Tap a card to open it and file a report. The active card zooms in to show full detail. You can filter by category and switch between daily/weekly/monthly/yearly views.</p>
                <h4>Charts</h4>
                <p>Swipe through the Stats section to see XP over time, category breakdown, and completion trends across any time period.</p>
              </SettingsInfoPage>
            )}

            {/* ── How Global Ranking works ── */}
            {settingsPage==="how-ranking"&&(
              <SettingsInfoPage title="GLOBAL RANKING" label="[ HOW IT WORKS ]"
                footer={!globalOptedIn?(
                  <button onClick={async()=>{
                    AudioEngine.click();
                    await supabase.from("profiles").update({global_ranking_opted_in:true}).eq("id",user.id);
                    setGlobalOptedIn(true);
                    setSettingsPage(null);
                    setActiveTab("ranks");
                  }} style={{width:"100%",padding:"14px",background:SYS_DIM,
                    border:`1px solid ${SYS_BORDER}`,borderRadius:10,color:SYS,
                    fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                    letterSpacing:2,cursor:"pointer",fontWeight:700,marginTop:8}}>
                    ✓ JOIN GLOBAL RANKING
                  </button>
                ):null}>
                <p>Global Ranking lets you compare your progress with all ARISE players worldwide on a standardized leaderboard.</p>
                <h4>Opt-in required</h4>
                <p>Global Ranking is optional. When you join, your player name, global level, global XP, and top category become visible to other players on the leaderboard.</p>
                <h4>Required categories</h4>
                <p>To participate, you must have at least one target in each of the 4 core categories: <strong>Work, Learning, Health, and Creative</strong>. This ensures fair comparison across all players.</p>
                <h4>Global XP vs Personal XP</h4>
                <p>Your personal XP uses whatever XP values you set per target. Global XP is standardized — every player's logs are scored using the same formula (log value ÷ weekly goal × 100 XP) regardless of personal settings. This keeps the leaderboard fair.</p>
                <h4>Same logs, two scores</h4>
                <p>You don't need to log anything separately. Every log you file on your Work/Learning/Health/Creative targets automatically counts toward both your personal XP and your global XP.</p>
                <h4>Global Level</h4>
                <p>Global level is calculated from your global XP using a fixed cost per level — base of 400 XP (4 categories × 100 XP). Every level costs the same, so consistent logging = consistent leveling for everyone.</p>
                <h4>Leaderboard view</h4>
                <p>The leaderboard shows your rank, name, global level, and strongest category. You always appear in the center of the list by default so you can see who's just above and below you.</p>
                <h4>Privacy</h4>
                <p>Your personal XP, individual logs, notes, email, and non-core targets are never shared. Only your name, global level, global XP, and top category are visible.</p>
              </SettingsInfoPage>
            )}

            {/* ── Privacy Policy ── */}
            {settingsPage==="privacy"&&(
              <SettingsInfoPage title="PRIVACY POLICY" label="[ DATA & PRIVACY ]">
                <p>Last updated: April 2026</p>
                <h4>What we collect</h4>
                <p>We collect your email address (for authentication), player name, task and log data, and XP/level information. If you opt into Global Ranking, your name, level, XP, and top category are visible to other users.</p>
                <h4>How we use your data</h4>
                <p>Your data is used solely to power ARISE — tracking your targets, calculating XP, and (if opted in) showing your rank on the leaderboard. We do not sell your data or share it with third parties.</p>
                <h4>Data storage</h4>
                <p>All data is stored securely on Supabase with row-level security policies. Only you can read and write your own task and profile data. Admins can view player names and send system messages.</p>
                <h4>Global Ranking</h4>
                <p>Participating in Global Ranking is entirely optional. If you opt in, your player name, global level, global XP, and strongest category become visible to all other players. You can opt out at any time by contacting the admin.</p>
                <h4>Messages</h4>
                <p>System messages sent by admins are stored in our database and visible only to you. Any replies you send are visible to the admin.</p>
                <h4>Account deletion</h4>
                <p>To delete your account and all associated data, contact the admin via the Contact Us section. We will process your request within 7 days.</p>
                <h4>Contact</h4>
                <p>For any privacy concerns, use the Contact Us option in Settings to reach the admin directly.</p>
              </SettingsInfoPage>
            )}

            {/* ── Contact Us ── */}
            {settingsPage==="contact"&&(
              <SettingsContactPage
                userId={user?.id} playerName={playerName} email={user?.email}
                sysMessages={sysMessages} userReplies={userReplies}
                onOpenInbox={()=>{
                  setSettingsPage(null);
                  setActiveTab("home");
                  setTimeout(()=>setShowMsgInboxAndRef(true),50);
                }}/>
            )}

            {/* ── How to Use ARISE ── */}
            {settingsPage==="how-to-use"&&(
              <SettingsInfoPage title="HOW TO USE ARISE" label="[ QUICK GUIDE ]">
                <h4>1. Create a Target</h4>
                <p>Tap <strong>+ New Target</strong> on the home screen. Give it a name, pick a category (Work, Health, Learning, etc.), choose a tracking metric (time, count, pages, etc.), and set your goal.</p>
                <h4>2. Log a Report</h4>
                <p>Tap any target card to open it, then tap <strong>Log Report</strong>. Enter what you completed — time spent, pages read, reps done — and mark the status. Each report earns you XP.</p>
                <h4>3. Earn XP & Level Up</h4>
                <p>Every report you log earns XP toward your hunter level. The more consistent you are, the faster you level up. Your current level and XP progress are shown at the top of the home screen.</p>
                <h4>4. Track Your Progress</h4>
                <p>Tap the chart icon on any target card to see stats — your completion rate, streaks, and history over daily, weekly, or monthly periods.</p>
                <h4>5. Join the Global Ranking</h4>
                <p>Go to <strong>Settings → How Global Ranking Works</strong> to opt in and compete with other hunters worldwide. Your global level and top category are displayed on the leaderboard.</p>
                <h4>6. Categories</h4>
                <p>You must set up at least one target in a category to appear in global rankings for that category. Categories: Work, Personal, Health, Learning, Creative, Other.</p>
                <h4>7. Premium Usernames</h4>
                <p>Want a gold glowing name? Go to <strong>Settings → How to Get Premium Username</strong> to learn how to request one.</p>
                <h4>Need Help?</h4>
                <p>Use <strong>Settings → Contact Us</strong> to send a message directly to the admin. We typically respond within 24 hours.</p>
              </SettingsInfoPage>
            )}

            {/* ── Main Settings Menu ── */}
            {!settingsPage&&(
              <>
                <div style={{marginBottom:24}}>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                    color:SYS,letterSpacing:3,marginBottom:4}}>[ SYSTEM ]</div>
                  <div style={{fontSize:22,fontWeight:800,color:"#fff",
                    fontFamily:"'Share Tech Mono',monospace",letterSpacing:2}}>SETTINGS</div>
                </div>

                {/* Player */}
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",
                  letterSpacing:2,marginBottom:8,marginTop:4}}>PLAYER</div>
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
                  <SettingsRow icon="👤" label="Change Username" sub="Update your player name"
                    onClick={()=>{AudioEngine.open();setSettingsPage("username");}}/>
                  <SettingsRow icon="⚡" label="Setup XP" sub="Adjust XP values per target"
                    onClick={()=>{AudioEngine.open();setSettingsPage("xp");}}/>
                </div>

                {/* Info */}
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",
                  letterSpacing:2,marginBottom:8}}>INFORMATION</div>
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
                  <SettingsRow icon="📖" label="How to Use ARISE" sub="Quick guide to get started"
                    onClick={()=>{AudioEngine.open();setSettingsPage("how-to-use");}}/>
                  <SettingsRow icon="✦" label="How to Get Premium Username" sub="Gold names, sword prefix & more"
                    onClick={()=>{AudioEngine.open();setSettingsPage("premium-info");}}/>
                  <SettingsRow icon="🎮" label="How ARISE Works" sub="Targets, reports, XP & leveling"
                    onClick={()=>{AudioEngine.open();setSettingsPage("how-arise");}}/>
                  <SettingsRow icon="🏆" label="How Global Ranking Works" sub={globalOptedIn?"You are ranked globally":"Learn & join the leaderboard"}
                    onClick={()=>{AudioEngine.open();setSettingsPage("how-ranking");}}/>
                </div>

                {/* Support */}
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",
                  letterSpacing:2,marginBottom:8}}>SUPPORT</div>
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
                  <SettingsRow icon="📋" label="Privacy Policy" sub="How we handle your data"
                    onClick={()=>{AudioEngine.open();setSettingsPage("privacy");}}/>
<SettingsRow icon="🔊"  label="AUDIO"  sub="Volume & mute controls"
  onClick={()=>{AudioEngine.open();setSettingsPage("audio");}}/>
                  <SettingsRow icon="🔔" label="Notifications" sub="Daily target reminders"
                    onClick={()=>{AudioEngine.open();setSettingsPage("notifications");}}/>
                  <SettingsRow icon="✉️" label="Contact Us" sub="Send a message to admin"
                    onClick={()=>{AudioEngine.open();setSettingsPage("contact");}}/>
                  {isAdmin&&(
                    <SettingsRow icon="🛡️" label="Admin Dashboard" sub="Manage players & messages" gold
                      onClick={()=>{AudioEngine.click();setActiveTab("home");setShowAdmin(true);}}/>
                  )}
                </div>

                {/* Sign out at bottom */}
                <div style={{marginTop:8}}>
                  <button onClick={()=>{AudioEngine.click();setConfirmSignOut(true);}}
                    style={{width:"100%",display:"flex",alignItems:"center",gap:14,
                      padding:"14px 16px",background:"rgba(239,68,68,0.05)",
                      border:"1px solid rgba(239,68,68,0.2)",borderRadius:10,cursor:"pointer"}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    <div style={{textAlign:"left"}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#fca5a5"}}>Sign Out</div>
                      <div style={{fontSize:11,color:"#64748b",marginTop:1}}>Exit the ARISE system</div>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
